/* ═══════════════════════════════════════════════════════════════════════════
   Shipping rate order — ABCD test

   Each Intelligems group promotes a different shipping option to the top of the
   list in the cart's delivery form. That position is not cosmetic: the first
   option is also the one auto-selected (see renderShippingOptions), so this is
   effectively a test of which shipping method customers are defaulted onto.

   ── TO ROLL BACK ─────────────────────────────────────────────────────────────
   Set SHIPPING_ORDER_TEST_ENABLED to false. That is the whole rollback — every
   group then falls through to the raw Shipzip API order, which is exactly what
   the cart did before this test. No markup, CSS or render logic was changed, and
   the pre-existing (dead) sortShippingRates method was deliberately left alone.
   ═══════════════════════════════════════════════════════════════════════════ */
const SHIPPING_ORDER_TEST_ENABLED = true;

// Fill in the group ids from the Intelligems dashboard — the same ids that appear
// in the `igTestGroups` cart attribute. `firstServiceCode` is the rate's
// service_code (not its display name); the first rate matching it is moved to the
// front and everything else keeps its API order. A group whose id isn't matched,
// or whose service_code isn't in the returned rates, falls back to API order.
const SHIPPING_ORDER_TEST_GROUPS = [
    { label: 'A', groupId: 'Original tracked', firstServiceCode: 'tracked_free_below_min' },
    { label: 'B', groupId: 'Original basic', firstServiceCode: 'free_below_min' },
    { label: 'C', groupId: '2 Way Checkout tracked', firstServiceCode: 'tracked_free_below_min' },
    { label: 'D', groupId: '2 Way Checkout basic', firstServiceCode: 'free_below_min' },
];

const SHIPPING_ORDER_QA_PARAM = 'ab_shipping_order';
const SHIPPING_ORDER_QA_KEY = 'lovepop_shipping_order_qa_override';

// Which group this visitor is in, or null for "leave the order alone".
// Mirrors is2WayCheckoutEnabled() in cart.js: QA override wins, then Intelligems
// group names, then nothing. Reads the group NAME rather than the experiment name —
// see the note in docs/ab-tests/2-way-checkout-rollout.md for why that matters.
window.getShippingOrderTestGroup = function () {
    if (!SHIPPING_ORDER_TEST_ENABLED) return null;

    try {
        const experiments = window.igData?.user?.getExperiments() || [];
        for (const experiment of experiments) {
            const groupName = window.igData.user.getTestGroupName(experiment.id) || '';
            const match = SHIPPING_ORDER_TEST_GROUPS.find(
                (g) => g.groupId && (groupName.includes(g.groupId) || experiment.groupId === g.groupId)
            );
            if (match) return match;
        }
    } catch (error) {
        console.error('Error resolving shipping order test group:', error);
    }

    return null;
};

// Moves this visitor's assigned service_code to the front. Returns a new array —
// the input is never mutated, so a caller that keeps the API order still has it.
window.applyShippingOrderTestSort = function (rates) {
    if (!Array.isArray(rates) || rates.length < 2) return rates;

    const group = window.getShippingOrderTestGroup();
    if (!group || !group.firstServiceCode) return rates;

    const index = rates.findIndex((rate) => rate && rate.service_code === group.firstServiceCode);
    if (index <= 0) return rates; // already first, or this group's option isn't offered here

    const reordered = rates.slice();
    const [promoted] = reordered.splice(index, 1);
    reordered.unshift(promoted);
    return reordered;
};

const ADDRESS_CONFIRMATION_OPTIONS = {
    USE_SUGGESTED: 'Use Suggested Address',
    KEEP_MINE: 'Keep My Address',
    EDIT: 'Edit Address',
};

const escapeAddressHtml = (value) => String(value)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');

// e.g. "287 W Johnson 1st Ave N, Anchorage, AK 99501"
const toSingleAddressLine = (addr) => [
    [addr.address, addr.apartment].filter(Boolean).join(' ').trim(),
    addr.city?.trim(),
    [addr.state, addr.zip_code].filter(Boolean).join(' ').trim(),
].filter(Boolean).join(', ');

// Splits into alternating [word, separator, word, separator, ...] so commas stay outside the highlights
const tokenizeAddressLine = (line) => line.split(/(,?\s+|,)/);

// Same rule as the comparison that opens the modal (case-insensitive only), so every difference that opens it gets highlighted
const normalizeAddressToken = (token) => token.toLowerCase();

// Marks the words of `source` that are not part of the longest common subsequence with `target`.
const getChangedWordFlags = (source, target) => {
    const a = source.map(normalizeAddressToken);
    const b = target.map(normalizeAddressToken);
    const lcs = Array.from({ length: a.length + 1 }, () => new Array(b.length + 1).fill(0));

    for (let i = a.length - 1; i >= 0; i--) {
        for (let j = b.length - 1; j >= 0; j--) {
            lcs[i][j] = a[i] === b[j] ? lcs[i + 1][j + 1] + 1 : Math.max(lcs[i + 1][j], lcs[i][j + 1]);
        }
    }

    const changed = new Array(a.length).fill(true);
    let i = 0, j = 0;
    while (i < a.length && j < b.length) {
        if (a[i] === b[j]) {
            changed[i] = false;
            i++;
            j++;
        } else if (lcs[i + 1][j] >= lcs[i][j + 1]) {
            i++;
        } else {
            j++;
        }
    }

    return changed;
};

// Wraps runs of changed words in one tag, so "1st Ave N" gets a single highlight
const renderAddressDiff = (line, otherLine, tag) => {
    const pieces = tokenizeAddressLine(line);
    const words = pieces.filter((_, index) => index % 2 === 0);
    const changed = getChangedWordFlags(words, tokenizeAddressLine(otherLine).filter((_, index) => index % 2 === 0));

    let html = '';
    let isOpen = false;
    pieces.forEach((piece, index) => {
        if (index % 2 === 0) {
            const isChanged = changed[index / 2];
            if (isChanged && !isOpen) {
                html += `<${tag} class="address-confirmation__diff">`;
                isOpen = true;
            }
            html += escapeAddressHtml(piece);
            const nextSeparator = pieces[index + 1];
            const nextChanged = changed[index / 2 + 1];
            if (isOpen && (!nextChanged || nextSeparator?.includes(','))) {
                html += `</${tag}>`;
                isOpen = false;
            }
        } else {
            html += escapeAddressHtml(piece);
        }
    });

    return html;
};

const renderAddressConfirmationRow = (label, addressHtml, modifier) =>
    `<div class="address-confirmation__row address-confirmation__row--${modifier}">`
    + `<span class="address-confirmation__label">${label}</span>`
    + `<span class="address-confirmation__address">${addressHtml}</span>`
    + `</div>`;

class DeliveryAddressForm extends HTMLElement {
    constructor() {
        super();
        this.shipzipService = window.shipzipService; // Use shared Shipzip service
        this.isOpen = false;
        this.currentStep = this.getInitialStep(); // Dynamic initial step
        this.addressSuggestions = [];
        this.address2Suggestions = [];
        this.debounceTimeout = null;
        this.smartyApiKey = "213388880343259109";
        this.addressData = {};
        this.shippingData = {};
        this.shippingRates = [];
        this.isLoadingShippingRates = false;
        this.customerAddresses = this.getCustomerAddresses();
        this.isEditing = false;
        this.isInitializing = false; // Flag to track initialization vs manual changes
        this.allowedCountries = null; // Cache for allowed countries based on cart items
        this.selectedAddressId = null; // Preserves the user's saved-address choice across step navigation
        this.confirmedAddressKey = null; // Address settled in the confirmation modal; skips re-validation while unchanged

        // Country names mapping
        this.countryNames = {
            'US': 'United States',
            'AU': 'Australia',
            'CA': 'Canada',
            'FR': 'France',
            'DE': 'Germany',
            'IE': 'Ireland',
            'JP': 'Japan',
            'CH': 'Switzerland',
            'GB': 'United Kingdom'
        };

        // Japanese prefecture mapping (Kanji to Romaji)
        this.japanPrefectureMap = {
            '北海道': 'Hokkaido',
            '青森県': 'Aomori', '青森': 'Aomori',
            '岩手県': 'Iwate', '岩手': 'Iwate',
            '宮城県': 'Miyagi', '宮城': 'Miyagi',
            '秋田県': 'Akita', '秋田': 'Akita',
            '山形県': 'Yamagata', '山形': 'Yamagata',
            '福島県': 'Fukushima', '福島': 'Fukushima',
            '茨城県': 'Ibaraki', '茨城': 'Ibaraki',
            '栃木県': 'Tochigi', '栃木': 'Tochigi',
            '群馬県': 'Gunma', '群馬': 'Gunma',
            '埼玉県': 'Saitama', '埼玉': 'Saitama',
            '千葉県': 'Chiba', '千葉': 'Chiba',
            '東京都': 'Tokyo', '東京': 'Tokyo',
            '神奈川県': 'Kanagawa', '神奈川': 'Kanagawa',
            '新潟県': 'Niigata', '新潟': 'Niigata',
            '富山県': 'Toyama', '富山': 'Toyama',
            '石川県': 'Ishikawa', '石川': 'Ishikawa',
            '福井県': 'Fukui', '福井': 'Fukui',
            '山梨県': 'Yamanashi', '山梨': 'Yamanashi',
            '長野県': 'Nagano', '長野': 'Nagano',
            '岐阜県': 'Gifu', '岐阜': 'Gifu',
            '静岡県': 'Shizuoka', '静岡': 'Shizuoka',
            '愛知県': 'Aichi', '愛知': 'Aichi',
            '三重県': 'Mie', '三重': 'Mie',
            '滋賀県': 'Shiga', '滋賀': 'Shiga',
            '京都府': 'Kyoto', '京都': 'Kyoto',
            '大阪府': 'Osaka', '大阪': 'Osaka',
            '兵庫県': 'Hyogo', '兵庫': 'Hyogo',
            '奈良県': 'Nara', '奈良': 'Nara',
            '和歌山県': 'Wakayama', '和歌山': 'Wakayama',
            '鳥取県': 'Tottori', '鳥取': 'Tottori',
            '島根県': 'Shimane', '島根': 'Shimane',
            '岡山県': 'Okayama', '岡山': 'Okayama',
            '広島県': 'Hiroshima', '広島': 'Hiroshima',
            '山口県': 'Yamaguchi', '山口': 'Yamaguchi',
            '徳島県': 'Tokushima', '徳島': 'Tokushima',
            '香川県': 'Kagawa', '香川': 'Kagawa',
            '愛媛県': 'Ehime', '愛媛': 'Ehime',
            '高知県': 'Kochi', '高知': 'Kochi',
            '福岡県': 'Fukuoka', '福岡': 'Fukuoka',
            '佐賀県': 'Saga', '佐賀': 'Saga',
            '長崎県': 'Nagasaki', '長崎': 'Nagasaki',
            '熊本県': 'Kumamoto', '熊本': 'Kumamoto',
            '大分県': 'Oita', '大分': 'Oita',
            '宮崎県': 'Miyazaki', '宮崎': 'Miyazaki',
            '鹿児島県': 'Kagoshima', '鹿児島': 'Kagoshima',
            '沖縄県': 'Okinawa', '沖縄': 'Okinawa'
        };

        this.iso2ToISO3 = {
            "AF": "AFG", "AX": "ALA", "AL": "ALB", "DZ": "DZA", "AS": "ASM", "AD": "AND", "AO": "AGO", "AI": "AIA", "AQ": "ATA", "AG": "ATG", "AR": "ARG", "AM": "ARM", "AW": "ABW", "AU": "AUS", "AT": "AUT", "AZ": "AZE", "BS": "BHS", "BH": "BHR", "BD": "BGD", "BB": "BRB", "BY": "BLR", "BE": "BEL", "BZ": "BLZ", "BJ": "BEN", "BM": "BMU", "BT": "BTN", "BO": "BOL", "BQ": "BES", "BA": "BIH", "BW": "BWA", "BV": "BVT", "BR": "BRA", "IO": "IOT", "BN": "BRN", "BG": "BGR", "BF": "BFA", "BI": "BDI", "CV": "CPV", "KH": "KHM", "CM": "CMR", "CA": "CAN", "KY": "CYM", "CF": "CAF", "TD": "TCD", "CL": "CHL", "CN": "CHN", "CX": "CXR", "CC": "CCK", "CO": "COL", "KM": "COM", "CG": "COG", "CD": "COD", "CK": "COK", "CR": "CRI", "CI": "CIV", "HR": "HRV", "CU": "CUB", "CW": "CUW", "CY": "CYP", "CZ": "CZE", "DK": "DNK", "DJ": "DJI", "DM": "DMA", "DO": "DOM", "EC": "ECU", "EG": "EGY", "SV": "SLV", "GQ": "GNQ", "ER": "ERI", "EE": "EST", "ET": "ETH", "FK": "FLK", "FO": "FRO", "FJ": "FJI", "FI": "FIN", "FR": "FRA", "GF": "GUF", "PF": "PYF", "TF": "ATF", "GA": "GAB", "GM": "GMB", "GE": "GEO", "DE": "DEU", "GH": "GHA", "GI": "GIB", "GR": "GRC", "GL": "GRL", "GD": "GRD", "GP": "GLP", "GU": "GUM", "GT": "GTM", "GG": "GGY", "GN": "GIN", "GW": "GNB", "GY": "GUY", "HT": "HTI", "HM": "HMD", "VA": "VAT", "HN": "HND", "HK": "HKG", "HU": "HUN", "IS": "ISL", "IN": "IND", "ID": "IDN", "IR": "IRN", "IQ": "IRQ", "IE": "IRL", "IM": "IMN", "IL": "ISR", "IT": "ITA", "JM": "JAM", "JP": "JPN", "JE": "JEY", "JO": "JOR", "KZ": "KAZ", "KE": "KEN", "KI": "KIR", "KP": "PRK", "KR": "KOR", "KW": "KWT", "KG": "KGZ", "LA": "LAO", "LV": "LVA", "LB": "LBN", "LS": "LSO", "LR": "LBR", "LY": "LBY", "LI": "LIE", "LT": "LTU", "LU": "LUX", "MO": "MAC", "MK": "MKD", "MG": "MDG", "MW": "MWI", "MY": "MYS", "MV": "MDV", "ML": "MLI", "MT": "MLT", "MH": "MHL", "MQ": "MTQ", "MR": "MRT", "MU": "MUS", "YT": "MYT", "MX": "MEX", "FM": "FSM", "MD": "MDA", "MC": "MCO", "MN": "MNG", "ME": "MNE", "MS": "MSR", "MA": "MAR", "MZ": "MOZ", "MM": "MMR", "NA": "NAM", "NR": "NRU", "NP": "NPL", "NL": "NLD", "NC": "NCL", "NZ": "NZL", "NI": "NIC", "NE": "NER", "NG": "NGA", "NU": "NIU", "NF": "NFK", "MP": "MNP", "NO": "NOR", "OM": "OMN", "PK": "PAK", "PW": "PLW", "PS": "PSE", "PA": "PAN", "PG": "PNG", "PY": "PRY", "PE": "PER", "PH": "PHL", "PN": "PCN", "PL": "POL", "PT": "PRT", "PR": "PRI", "QA": "QAT", "RE": "REU", "RO": "ROU", "RU": "RUS", "RW": "RWA", "BL": "BLM", "SH": "SHN", "KN": "KNA", "LC": "LCA", "MF": "MAF", "PM": "SPM", "VC": "VCT", "WS": "WSM", "SM": "SMR", "ST": "STP", "SA": "SAU", "SN": "SEN", "RS": "SRB", "SC": "SYC", "SL": "SLE", "SG": "SGP", "SX": "SXM", "SK": "SVK", "SI": "SVN", "SB": "SLB", "SO": "SOM", "ZA": "ZAF", "GS": "SGS", "SS": "SSD", "ES": "ESP", "LK": "LKA", "SD": "SDN", "SR": "SUR", "SJ": "SJM", "SZ": "SWZ", "SE": "SWE", "CH": "CHE", "SY": "SYR", "TW": "TWN", "TJ": "TJK", "TZ": "TZA", "TH": "THA", "TL": "TLS", "TG": "TGO", "TK": "TKL", "TO": "TON", "TT": "TTO", "TN": "TUN", "TR": "TUR", "TM": "TKM", "TC": "TCA", "TV": "TUV", "UG": "UGA", "UA": "UKR", "AE": "ARE", "GB": "GBR", "US": "USA", "UM": "UMI", "UY": "URY", "UZ": "UZB", "VU": "VUT", "VE": "VEN", "VN": "VNM", "VG": "VGB", "VI": "VIR", "WF": "WLF", "EH": "ESH", "YE": "YEM", "ZM": "ZMB", "ZW": "ZWE"
        };
        this.countryStates = {
            'US': [
                { value: 'AL', label: 'Alabama' },
                { value: 'AK', label: 'Alaska' },
                { value: 'AS', label: 'American Samoa' },
                { value: 'AZ', label: 'Arizona' },
                { value: 'AR', label: 'Arkansas' },
                { value: 'CA', label: 'California' },
                { value: 'CO', label: 'Colorado' },
                { value: 'CT', label: 'Connecticut' },
                { value: 'DE', label: 'Delaware' },
                { value: 'DC', label: 'District of Columbia' },
                { value: 'FM', label: 'Federated States of Micronesia' },
                { value: 'FL', label: 'Florida' },
                { value: 'GA', label: 'Georgia' },
                { value: 'GU', label: 'Guam' },
                { value: 'HI', label: 'Hawaii' },
                { value: 'ID', label: 'Idaho' },
                { value: 'IL', label: 'Illinois' },
                { value: 'IN', label: 'Indiana' },
                { value: 'IA', label: 'Iowa' },
                { value: 'KS', label: 'Kansas' },
                { value: 'KY', label: 'Kentucky' },
                { value: 'LA', label: 'Louisiana' },
                { value: 'ME', label: 'Maine' },
                { value: 'MH', label: 'Marshall Islands' },
                { value: 'MD', label: 'Maryland' },
                { value: 'MA', label: 'Massachusetts' },
                { value: 'MI', label: 'Michigan' },
                { value: 'MN', label: 'Minnesota' },
                { value: 'MS', label: 'Mississippi' },
                { value: 'MO', label: 'Missouri' },
                { value: 'MT', label: 'Montana' },
                { value: 'NE', label: 'Nebraska' },
                { value: 'NV', label: 'Nevada' },
                { value: 'NH', label: 'New Hampshire' },
                { value: 'NJ', label: 'New Jersey' },
                { value: 'NM', label: 'New Mexico' },
                { value: 'NY', label: 'New York' },
                { value: 'NC', label: 'North Carolina' },
                { value: 'ND', label: 'North Dakota' },
                { value: 'MP', label: 'Northern Mariana Islands' },
                { value: 'OH', label: 'Ohio' },
                { value: 'OK', label: 'Oklahoma' },
                { value: 'OR', label: 'Oregon' },
                { value: 'PW', label: 'Palau' },
                { value: 'PA', label: 'Pennsylvania' },
                { value: 'PR', label: 'Puerto Rico' },
                { value: 'RI', label: 'Rhode Island' },
                { value: 'SC', label: 'South Carolina' },
                { value: 'SD', label: 'South Dakota' },
                { value: 'TN', label: 'Tennessee' },
                { value: 'TX', label: 'Texas' },
                { value: 'UT', label: 'Utah' },
                { value: 'VT', label: 'Vermont' },
                { value: 'VI', label: 'Virgin Islands' },
                { value: 'VA', label: 'Virginia' },
                { value: 'WA', label: 'Washington' },
                { value: 'WV', label: 'West Virginia' },
                { value: 'WI', label: 'Wisconsin' },
                { value: 'WY', label: 'Wyoming' },
                { value: 'AA', label: 'Armed Forces Americas' },
                { value: 'AE', label: 'Armed Forces Europe' },
                { value: 'AP', label: 'Armed Forces Pacific' }
            ],
            'CA': [
                { value: 'AB', label: 'Alberta' },
                { value: 'BC', label: 'British Columbia' },
                { value: 'MB', label: 'Manitoba' },
                { value: 'NB', label: 'New Brunswick' },
                { value: 'NL', label: 'Newfoundland and Labrador' },
                { value: 'NT', label: 'Northwest Territories' },
                { value: 'NS', label: 'Nova Scotia' },
                { value: 'NU', label: 'Nunavut' },
                { value: 'ON', label: 'Ontario' },
                { value: 'PE', label: 'Prince Edward Island' },
                { value: 'QC', label: 'Quebec' },
                { value: 'SK', label: 'Saskatchewan' },
                { value: 'YT', label: 'Yukon' }
            ],
            'AU': [
                { value: 'ACT', label: 'Australian Capital Territory' },
                { value: 'NSW', label: 'New South Wales' },
                { value: 'NT', label: 'Northern Territory' },
                { value: 'QLD', label: 'Queensland' },
                { value: 'SA', label: 'South Australia' },
                { value: 'TAS', label: 'Tasmania' },
                { value: 'VIC', label: 'Victoria' },
                { value: 'WA', label: 'Western Australia' }
            ],
            'JP': [
                { value: 'Hokkaido', label: '北海道 (Hokkaido)', romaji: 'Hokkaido' },
                { value: 'Aomori', label: '青森県 (Aomori)', romaji: 'Aomori' },
                { value: 'Iwate', label: '岩手県 (Iwate)', romaji: 'Iwate' },
                { value: 'Miyagi', label: '宮城県 (Miyagi)', romaji: 'Miyagi' },
                { value: 'Akita', label: '秋田県 (Akita)', romaji: 'Akita' },
                { value: 'Yamagata', label: '山形県 (Yamagata)', romaji: 'Yamagata' },
                { value: 'Fukushima', label: '福島県 (Fukushima)', romaji: 'Fukushima' },
                { value: 'Ibaraki', label: '茨城県 (Ibaraki)', romaji: 'Ibaraki' },
                { value: 'Tochigi', label: '栃木県 (Tochigi)', romaji: 'Tochigi' },
                { value: 'Gunma', label: '群馬県 (Gunma)', romaji: 'Gunma' },
                { value: 'Saitama', label: '埼玉県 (Saitama)', romaji: 'Saitama' },
                { value: 'Chiba', label: '千葉県 (Chiba)', romaji: 'Chiba' },
                { value: 'Tokyo', label: '東京都 (Tokyo)', romaji: 'Tokyo' },
                { value: 'Kanagawa', label: '神奈川県 (Kanagawa)', romaji: 'Kanagawa' },
                { value: 'Niigata', label: '新潟県 (Niigata)', romaji: 'Niigata' },
                { value: 'Toyama', label: '富山県 (Toyama)', romaji: 'Toyama' },
                { value: 'Ishikawa', label: '石川県 (Ishikawa)', romaji: 'Ishikawa' },
                { value: 'Fukui', label: '福井県 (Fukui)', romaji: 'Fukui' },
                { value: 'Yamanashi', label: '山梨県 (Yamanashi)', romaji: 'Yamanashi' },
                { value: 'Nagano', label: '長野県 (Nagano)', romaji: 'Nagano' },
                { value: 'Gifu', label: '岐阜県 (Gifu)', romaji: 'Gifu' },
                { value: 'Shizuoka', label: '静岡県 (Shizuoka)', romaji: 'Shizuoka' },
                { value: 'Aichi', label: '愛知県 (Aichi)', romaji: 'Aichi' },
                { value: 'Mie', label: '三重県 (Mie)', romaji: 'Mie' },
                { value: 'Shiga', label: '滋賀県 (Shiga)', romaji: 'Shiga' },
                { value: 'Kyoto', label: '京都府 (Kyoto)', romaji: 'Kyoto' },
                { value: 'Osaka', label: '大阪府 (Osaka)', romaji: 'Osaka' },
                { value: 'Hyogo', label: '兵庫県 (Hyogo)', romaji: 'Hyogo' },
                { value: 'Nara', label: '奈良県 (Nara)', romaji: 'Nara' },
                { value: 'Wakayama', label: '和歌山県 (Wakayama)', romaji: 'Wakayama' },
                { value: 'Tottori', label: '鳥取県 (Tottori)', romaji: 'Tottori' },
                { value: 'Shimane', label: '島根県 (Shimane)', romaji: 'Shimane' },
                { value: 'Okayama', label: '岡山県 (Okayama)', romaji: 'Okayama' },
                { value: 'Hiroshima', label: '広島県 (Hiroshima)', romaji: 'Hiroshima' },
                { value: 'Yamaguchi', label: '山口県 (Yamaguchi)', romaji: 'Yamaguchi' },
                { value: 'Tokushima', label: '徳島県 (Tokushima)', romaji: 'Tokushima' },
                { value: 'Kagawa', label: '香川県 (Kagawa)', romaji: 'Kagawa' },
                { value: 'Ehime', label: '愛媛県 (Ehime)', romaji: 'Ehime' },
                { value: 'Kochi', label: '高知県 (Kochi)', romaji: 'Kochi' },
                { value: 'Fukuoka', label: '福岡県 (Fukuoka)', romaji: 'Fukuoka' },
                { value: 'Saga', label: '佐賀県 (Saga)', romaji: 'Saga' },
                { value: 'Nagasaki', label: '長崎県 (Nagasaki)', romaji: 'Nagasaki' },
                { value: 'Kumamoto', label: '熊本県 (Kumamoto)', romaji: 'Kumamoto' },
                { value: 'Oita', label: '大分県 (Oita)', romaji: 'Oita' },
                { value: 'Miyazaki', label: '宮崎県 (Miyazaki)', romaji: 'Miyazaki' },
                { value: 'Kagoshima', label: '鹿児島県 (Kagoshima)', romaji: 'Kagoshima' },
                { value: 'Okinawa', label: '沖縄県 (Okinawa)', romaji: 'Okinawa' }
            ],
            'IE': [
                { value: 'CW', label: 'Carlow' },
                { value: 'CN', label: 'Cavan' },
                { value: 'CE', label: 'Clare' },
                { value: 'CO', label: 'Cork' },
                { value: 'DL', label: 'Donegal' },
                { value: 'D', label: 'Dublin' },
                { value: 'G', label: 'Galway' },
                { value: 'KY', label: 'Kerry' },
                { value: 'KE', label: 'Kildare' },
                { value: 'KK', label: 'Kilkenny' },
                { value: 'LS', label: 'Laois' },
                { value: 'LM', label: 'Leitrim' },
                { value: 'LK', label: 'Limerick' },
                { value: 'LD', label: 'Longford' },
                { value: 'LH', label: 'Louth' },
                { value: 'MO', label: 'Mayo' },
                { value: 'MH', label: 'Meath' },
                { value: 'MN', label: 'Monaghan' },
                { value: 'OY', label: 'Offaly' },
                { value: 'RN', label: 'Roscommon' },
                { value: 'SO', label: 'Sligo' },
                { value: 'TA', label: 'Tipperary' },
                { value: 'WD', label: 'Waterford' },
                { value: 'WH', label: 'Westmeath' },
                { value: 'WX', label: 'Wexford' },
                { value: 'WW', label: 'Wicklow' }
            ]
        };
        this.init();
    }

    // Determine initial step based on user login status and address data
    getInitialStep() {
        const isLoggedIn = window.customer && window.customer.id;
        const hasExistingAddress = window.stm_init_data && (window.stm_init_data._first_name || window.stm_init_data._address1);

        if (!isLoggedIn) {
            return 1; // Go directly to address form for guests
        }

        if (hasExistingAddress) {
            return 1; // Go to address form pre-filled for logged-in users with existing address
        }

        return 0; // Go to address selection for logged-in users without address
    }

    getCustomerAddresses() {
        // First check if addresses are set via JavaScript
        if (window.customerAddresses && window.customerAddresses.length > 0) {
            return window.customerAddresses;
        }

        // Then check if customer object has addresses (from Liquid)
        if (window.customer && window.customer.addresses && window.customer.addresses.length > 0) {
            return window.customer.addresses;
        }

        // Finally check if addresses are in the STM init data
        if (window.stm_init_data && window.stm_init_data.customer_addresses) {
            return window.stm_init_data.customer_addresses;
        }

        return [];
    }

    init() {
        this.innerHTML = this.getTemplate();
        this.setupEventListeners();
        this.setupAddressAutocomplete();
    }

    getTemplate() {
        return `
      <div class="delivery-address-overlay" style="display: none;">
        <div class="delivery-address-container">
          <div class="delivery-address-header">
            <button type="button" class="delivery-address-back-btn">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                <path d="M15 18L9 12L15 6" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
            </button>
            <h2 class="delivery-address-title">Where is the Card Going?</h2>
            <button type="button" class="delivery-address-close-btn">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                <path d="M18 6L6 18M6 6L18 18" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
            </button>
          </div>
          <form class="delivery-address-form">
            <!-- Step 0: Address Selection (Logged-in users only) -->
            <div class="delivery-address-step step-0" ${this.customerAddresses && this.customerAddresses.length > 0 ? '' : 'style="display: none;"'}>
              <div class="address-selection">
                <div class="address-list-wrapper">
                  <button type="button" class="delivery-address-new-address-btn">
                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M8.00065 13.3334C5.06065 13.3334 2.66732 10.94 2.66732 8.00004C2.66732 5.06004 5.06065 2.66671 8.00065 2.66671C10.9406 2.66671 13.334 5.06004 13.334 8.00004C13.334 10.94 10.9406 13.3334 8.00065 13.3334ZM8.00065 1.33337C7.12517 1.33337 6.25827 1.50581 5.44943 1.84084C4.64059 2.17588 3.90566 2.66694 3.28661 3.286C2.03636 4.53624 1.33398 6.23193 1.33398 8.00004C1.33398 9.76815 2.03636 11.4638 3.28661 12.7141C3.90566 13.3331 4.64059 13.8242 5.44943 14.1592C6.25827 14.4943 7.12517 14.6667 8.00065 14.6667C9.76876 14.6667 11.4645 13.9643 12.7147 12.7141C13.9649 11.4638 14.6673 9.76815 14.6673 8.00004C14.6673 7.12456 14.4949 6.25765 14.1598 5.44882C13.8248 4.63998 13.3338 3.90505 12.7147 3.286C12.0956 2.66694 11.3607 2.17588 10.5519 1.84084C9.74304 1.50581 8.87613 1.33337 8.00065 1.33337ZM8.66732 4.66671H7.33398V7.33337H4.66732V8.66671H7.33398V11.3334H8.66732V8.66671H11.334V7.33337H8.66732V4.66671Z" fill="#096DD9"/>
                    </svg>
                    <span>Enter New Address</span>
                  </button>
                  <div class="saved-addresses">
                    <!-- Addresses will be populated by JavaScript -->
                  </div>
                </div>

                <button type="button" class="delivery-address-use-selected-btn">
                  Use Selected Address
                </button>
              </div>
            </div>

            <!-- Step 1: Address Form -->
            <div class="delivery-address-step step-1" ${this.customerAddresses && this.customerAddresses.length > 0 ? 'style="display: none;"' : ''}>
                <div class="form-field">
                    <label for="country">Country</label>
                        <select id="country" name="country" required>
                        <option value="US" selected>United States</option>
                        <option value="AU">Australia</option>
                        <option value="CA">Canada</option>
                        <option value="FR">France</option>
                        <option value="DE">Germany</option>
                        <option value="IE">Ireland</option>
                        <option value="JP">Japan</option>
                        <option value="CH">Switzerland</option>
                        <option value="GB">United Kingdom</option>
                    </select>
                </div>
                <div class="form-row">
                    <div class="form-field">
                    <label for="first-name">First name</label>
                    <input type="text" id="first-name" name="first_name" required>
                    </div>
                    <div class="form-field">
                    <label for="last-name">Last name</label>
                    <input type="text" id="last-name" name="last_name" required>
                    </div>
                </div>
                <div class="form-field">
                    <label for="address">Address</label>
                    <input type="text" id="address" name="address" required>
                </div>
                <div class="form-field">
                    <label for="apartment">Apartment, suite, etc. (optional)</label>
                    <input type="text" id="apartment" name="apartment">
                </div>

                <div class="form-row">
                    <div class="form-field">
                        <label for="city">City</label>
                        <input type="text" id="city" name="city" required>
                    </div>
                    <div class="form-field">
                        <label for="state">State</label>
                        <select id="state" name="state" required>
                            <option value="">Select State</option>
                            <option value="MA">MA</option>
                            <option value="CA">CA</option>
                            <option value="NY">NY</option>
                            <option value="TX">TX</option>
                            <option value="FL">FL</option>
                            <!-- Add more states as needed -->
                        </select>
                    </div>
                </div>

               <div class="form-field">
                  <label for="zip-code">Zip code</label>
                  <input type="text" id="zip-code" name="zip_code" required>
                </div>
              <div class="step-navigation-buttons">
                <button type="button" class="delivery-address-continue-btn">
                  Add Delivery Address
                </button>
                <button type="button" class="delivery-address-cancel-btn">
                  Go Back
                </button>
              </div>
            </div>

            <!-- Step 2: Shipping Method Selection -->
            <div class="delivery-address-step step-2" style="display: none;">
              <div class="shipping-methods">
                <h3>Shipping method</h3>
                <div class="shipping-warning-message" style="display: none;"></div>
                <div class="shipping-options">

                </div>
              </div>

              <button type="submit" class="delivery-address-submit-btn">
                Submit
              </button>
              <button type="button" class="delivery-address-go-back-btn">
                Go Back
              </button>
            </div>
          </form>
        </div>
      </div>

      <div class="address-confirmation" hidden>
        <div class="address-confirmation__dialog" role="dialog" aria-modal="true" aria-labelledby="address-confirmation-title" aria-describedby="address-confirmation-description">
          <div class="address-confirmation__header">
            <h2 id="address-confirmation-title" class="address-confirmation__title">Address Confirmation</h2>
            <button type="button" class="address-confirmation__close" aria-label="Close">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                <path d="M18 6L6 18M6 6L18 18" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
            </button>
          </div>
          <div class="address-confirmation__body" id="address-confirmation-description">
            <p class="address-confirmation__intro">We found a possible issue with the address you entered.</p>
            <div class="address-confirmation__box"></div>
          </div>
          <div class="address-confirmation__actions">
            <button type="button" class="address-confirmation__btn address-confirmation__btn--primary" data-option="${ADDRESS_CONFIRMATION_OPTIONS.USE_SUGGESTED}">${ADDRESS_CONFIRMATION_OPTIONS.USE_SUGGESTED}</button>
            <button type="button" class="address-confirmation__btn address-confirmation__btn--secondary" data-option="${ADDRESS_CONFIRMATION_OPTIONS.KEEP_MINE}">${ADDRESS_CONFIRMATION_OPTIONS.KEEP_MINE}</button>
            <button type="button" class="address-confirmation__btn address-confirmation__btn--text" data-option="${ADDRESS_CONFIRMATION_OPTIONS.EDIT}">${ADDRESS_CONFIRMATION_OPTIONS.EDIT}</button>
          </div>
        </div>
      </div>
    `;
    }

    setupEventListeners() {
        const overlay = this.querySelector('.delivery-address-overlay');
        const closeBtn = this.querySelector('.delivery-address-close-btn');
        const backBtn = this.querySelector('.delivery-address-back-btn');
        const cancelBtn = this.querySelector('.delivery-address-cancel-btn');
        const continueBtn = this.querySelector('.delivery-address-continue-btn');
        const goBackBtn = this.querySelector('.delivery-address-go-back-btn');
        const form = this.querySelector('.delivery-address-form');

        // Close button
        closeBtn.addEventListener('click', () => this.close());

        // Header back button (closes modal)
        backBtn.addEventListener('click', () => {
            if (this.currentStep === 1 && this.customerAddresses && this.customerAddresses.length > 0) {
                this.goToStep(0);
            } else if (this.currentStep === 2) {
                this.goToStep(1);
            } else {
                this.close();
            }
        });

        // Cancel button (closes modal)
        cancelBtn.addEventListener('click', () => {
            if (this.currentStep === 1 && this.customerAddresses && this.customerAddresses.length > 0) {
                this.goToStep(0);
            } else {
                this.close();
            }
        });

        // Continue button (step 1 to step 2)
        continueBtn.addEventListener('click', (e) => {
            e.preventDefault();
            this.handleStep1Continue();
            //trigger new heap event
            if (this.isEditing) {
                window.heapGlobalNew?.trackEditAddressSave?.();
            } else {
                window.heapGlobalNew?.trackSendToMeSubmitAddress?.();
            }
        });

        // Go back button (step 2 to step 1)
        goBackBtn.addEventListener('click', (e) => {
            e.preventDefault();
            this.goToStep(1);
        });

        // Address selection buttons
        const useSelectedBtn = this.querySelector('.delivery-address-use-selected-btn');
        const newAddressBtn = this.querySelector('.delivery-address-new-address-btn');

        if (useSelectedBtn) {
            useSelectedBtn.addEventListener('click', (e) => {
                e.preventDefault();
                this.handleUseSelectedAddress();
            });
        }

        if (newAddressBtn) {
            newAddressBtn.addEventListener('click', (e) => {
                e.preventDefault();
                this.handleEnterNewAddress();
            });
        }

        // Back to addresses button (in step 1)
        const backToAddressesBtn = this.querySelector('.delivery-address-back-to-addresses-btn');
        if (backToAddressesBtn) {
            backToAddressesBtn.addEventListener('click', (e) => {
                e.preventDefault();
                this.goToStep(0);
            });
        }

        // Close on overlay click
        overlay.addEventListener('click', (e) => {
            if (e.target === overlay) {
                this.close();
            }
        });

        // Form submission (final step)
        form.addEventListener('submit', (e) => {
            e.preventDefault();
            this.handleFinalSubmit();
        });

        // Listen for open event from trigger buttons
        document.addEventListener('click', (e) => {
            // Send-to-me carts keep this trigger hidden (display:none) via CSS, but
            // guard anyway in case of stale/cached markup.
            if (!window.getCartRequiresAddressFlow?.()) return;

            if (e.target.classList.contains('cart__add-delivery-address-btn')) {
                e.preventDefault();
                this.open();
            }

            // Handle edit button clicks
            if (e.target.classList.contains('cart-delivery-stm-edit') || e.target.classList.contains('cart-delivery-fill-footer-text')) {
                e.preventDefault();
                this.openWithEditData();
            }

            if (e.target.closest('.cart-delivery-stm-edit-shipping')) {
                e.preventDefault();
                //remove old heap event
                // window.heapGlobal?.trackEvent(
                //     "edit_address_start",
                //     {
                //         Page: window.getPageForAnalytics(),
                //         Category: 'Personalization',
                //         Type: 'Custom event',
                //         Version: window._lovepop_version,
                //         Source: window.getPageForAnalytics(),
                //         "Select Option": 'Send to me'
                //     }
                // );
                //trigger new heap event
                window.heapGlobalNew?.trackEditAddressStart?.();
                this.openWithEditData(1);
            }

            if (e.target.closest('.cart-delivery-stm-edit-shipping-method')) {
                e.preventDefault();
                //remove old heap event
                // window.heapGlobal?.trackEvent(
                //     "edit_shipping_method_start",
                //     {
                //         Page: window.getPageForAnalytics(),
                //         Category: 'Personalization',
                //         Type: 'Custom event',
                //         Version: window._lovepop_version,
                //         Source: window.getPageForAnalytics(),
                //         "Select Option": 'Send to me'
                //     }
                // );

                //trigger new heap event
                window.heapGlobalNew?.trackEditShippingMethodStart?.();
                this.openWithEditData(2);
            }
        });
    }

    async openWithEditData(step = 1) {
        const stmData = window.stm_init_data;

        if (stmData && typeof stmData === 'object') {
            // Check if we have address data
            const hasAddressData = stmData._first_name || stmData._address1 || stmData._city;

            if (hasAddressData) {
                // Validate if the current country is allowed for all products
                const selectedCountry = stmData._country;
                if (selectedCountry && window.validateDeliveryCountryForProducts) {
                    const isValid = await window.validateDeliveryCountryForProducts(selectedCountry);

                    if (!isValid.valid) {
                        // Open with error if country is not allowed
                        this.openWithError({
                            country: selectedCountry,
                            restrictedProducts: isValid.restrictedProducts
                        });
                        return;
                    }
                }
                
                // console.log('Opening with existing data:', stmData);
                this.open(true);
                this.initializeWithData(stmData);
                this.goToStep(step);
            } else {
                // console.log('No existing address data, opening empty form');
                this.open();
            }
        } else {
            // console.log('No STM data found, opening empty form');
            this.open();
        }
    }

    // Smarty API Methods
    async fetchAutocomplete(searchQuery, countryCode, selected = null) {
        if (!searchQuery) return [];

        const baseUrlUS = "https://us-autocomplete-pro.api.smarty.com/lookup";
        const baseUrlInt = "https://international-autocomplete.api.smarty.com/v2/lookup";

        let query = '';

        if (countryCode === "US") {
            query = `${baseUrlUS}?key=${this.smartyApiKey}&search=${encodeURIComponent(searchQuery)}${selected ? `&selected=${encodeURIComponent(selected)}` : ''}`;
        } else {
            query = `${baseUrlInt}?key=${this.smartyApiKey}&search=${encodeURIComponent(searchQuery)}&country=${this.iso2ToISO3[countryCode]}`;
        }

        try {
            const response = await fetch(query, { method: 'GET', mode: 'cors' });
            return await response.json();
        } catch (error) {
            console.error('Error fetching autocomplete:', error);
            return [];
        }
    }

    async verifyAddressNonUS(addressId, countryCode) {
        const baseUrlInt = "https://international-autocomplete.api.smarty.com/v2/lookup";
        let query = `${baseUrlInt}/${addressId}?key=${this.smartyApiKey}&country=${this.iso2ToISO3[countryCode]}`;

        try {
            const response = await fetch(query, { method: 'GET', mode: 'cors' });
            let data = await response.json();
            let address = data.candidates[0];

            if (address && address.entries) {
                query = `${baseUrlInt}/${address.address_id}?key=${this.smartyApiKey}&country=${this.iso2ToISO3[countryCode]}`;
                const res = await fetch(query);
                const newData = await res.json();
                address = newData.candidates[0];
            }

            return address;
        } catch (error) {
            console.error('Error verifying non-US address:', error);
            return null;
        }
    }

    async validateAddressUS(street, city, state, zip, address2) {
        const baseValidateUS = "https://us-street.api.smarty.com/street-address";
        let query = `${baseValidateUS}?key=${this.smartyApiKey}&street=${encodeURIComponent(street)}&city=${encodeURIComponent(city)}&state=${encodeURIComponent(state)}&zipcode=${encodeURIComponent(zip)}&secondary=${encodeURIComponent(address2)}`;

        try {
            const response = await fetch(query, { method: 'GET', mode: 'cors' });
            return await response.json();
        } catch (error) {
            console.error('Error validating US address:', error);
            return [];
        }
    }

    // Enhanced address validation with user confirmation
    async validateAndConfirmAddress(isFormattedCheck = false) {
        try {
            if (this.addressData.country !== 'US') {
                return { isValid: true, addressData: this.addressData };
            }

            const validation = await this.validateAddressUS(
                this.addressData.address,
                this.addressData.city,
                this.addressData.state,
                this.addressData.zip_code,
                this.addressData.apartment || ''
            );

            if (validation && validation.length > 0) {
                const validatedAddress = validation[0];

                // Check if address is deliverable
                if (validatedAddress.analysis && validatedAddress.analysis.active === 'Y') {
                    const components = validatedAddress.components;
                    const suggestedAddress = {
                        address: validatedAddress.delivery_line_1,
                        city: components.default_city_name,
                        apartment: '', // keep it empty because already include in address
                        state: components.state_abbreviation,
                        zip_code: components.zipcode,
                        country: 'US',
                        first_name: this.addressData.first_name,
                        last_name: this.addressData.last_name
                    };

                    // Build address strings for comparison
                    const originalFormatted = this.buildFormattedAddress(this.addressData);
                    const suggestedFormatted = this.buildFormattedAddress(suggestedAddress);

                    // Check if the addresses are significantly different
                    const shouldShowConfirmation = originalFormatted.toLowerCase() !== suggestedFormatted.toLowerCase();
                    const secondaryAddress = validatedAddress.components.secondary_designator ? (validatedAddress.components.secondary_designator + " " + validatedAddress.components.secondary_number) : this.addressData.apartment;

                    suggestedAddress.apartment = secondaryAddress;
                    suggestedAddress.address = suggestedAddress.address?.replace(secondaryAddress, "").trim()

                    if (shouldShowConfirmation && !isFormattedCheck) {
                        return {
                            isValid: true,
                            requiresConfirmation: true,
                            originalAddress: this.addressData,
                            suggestedAddress: suggestedAddress,
                            validatedData: validatedAddress
                        };
                    }

                    return {
                        isValid: true,
                        addressData: suggestedAddress,
                        validatedData: validatedAddress
                    };
                } else {
                    // Address exists but might not be deliverable
                    return {
                        isValid: false,
                        error: 'Address validation failed. The address may not be deliverable.',
                        addressData: this.addressData
                    };
                }
            } else {
                // No validation results found
                return {
                    isValid: false,
                    error: "We can't find this address. Please verify it's correct.",
                    addressData: this.addressData
                };
            }
        } catch (error) {
            console.error('Error in address validation:', error);
            return {
                isValid: false,
                error: 'Unable to validate address due to a technical error.',
                addressData: this.addressData
            };
        }
    }

    // Address-save API endpoint (mirrors ShipzipService.getEndpoint()'s env pattern)
    getAddressSaveEndpoint() {
        const location = window.location.origin;
        let env = '';

        if (location.includes('test')) {
            env = 'test';
        } else if (location.includes('staging')) {
            env = 'staging';
        } else if (location.includes('prod')) {
            env = 'prod';
        }

        let resolvedUrl;
        switch (env) {
            case 'test':
            case 'staging':
                resolvedUrl = 'https://bt8x9lvpe3.execute-api.us-west-2.amazonaws.com/dev/thanhgiong/api/Address';
                break;
            case 'prod':
                resolvedUrl = 'https://57rcqblb3f.execute-api.us-west-2.amazonaws.com/prod/thanhgiong/api/Address';
                break;
            default:
                resolvedUrl = 'https://57rcqblb3f.execute-api.us-west-2.amazonaws.com/prod/thanhgiong/api/Address';
                break;
        }
        return resolvedUrl;
    }

    // Persist the entered address to Shopify's customer address book via the
    // personalization backend. No-ops for guests (no customer to attach it to).
    // Non-blocking: logs on failure but never stops the delivery flow.
    async saveAddressToBackend(addressData) {
        const customerId = window.customer && window.customer.id;

        if (!customerId) {
            return null;
        }

        const payload = {
            firstName: addressData.first_name,
            lastName: addressData.last_name,
            address: addressData.address,
            apt: addressData.apartment || '',
            city: addressData.city,
            stateProvince: addressData.state || '',
            postalCode: addressData.zip_code,
            countryCode: addressData.country,
            shopifyCustomerId: customerId,
            addressType: 0
        };

        // This endpoint requires the same 3 headers personalization-fe's axiosClient
        // sets globally (see MainPersonalizationLite.tsx). The x-api-key is a fixed
        // value shipped client-side there; the Authorization token is set globally by
        // snippets/personalization-variables.liquid (window.personalization.accessToken),
        // same global the personalization widget itself reads.
        const accessToken = window.personalization?.editingAccesstoken || window.personalization?.accessToken || null;
        if (!accessToken) {
            console.warn('[saveAddressToBackend] No accessToken found on window.personalization.accessToken - request will likely 401. Set this to the same token used by the personalization widget elsewhere on the page.');
        }

        const authHeaders = {
            'x-api-key': 'b9qyntyVhq66WtjhA2JLt7YIcdVtLK8Si5RnctW3',
            'cid': customerId,
            'Authorization': accessToken
        };

        try {
            const endpoint = this.getAddressSaveEndpoint();
            const response = await fetch(endpoint, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    Accept: 'application/json',
                    ...authHeaders
                },
                body: JSON.stringify(payload)
            });

            if (!response.ok) {
                const errorBody = await response.text().catch(() => '(could not read body)');
                console.error('[saveAddressToBackend] Failed to save address to backend:', response.status, response.statusText, errorBody);
                return null;
            }

            const savedAddress = await response.json();

            // Merge the saved address into the in-memory session list so it shows
            // up immediately if the shopper reopens "Choose Shipping Address".
            const propertyId = savedAddress.shopifyAddressId ? 'shopifyAddressId' : 'id';
            const sessionAddress = {
                id: savedAddress[propertyId],
                first_name: savedAddress.firstName,
                last_name: savedAddress.lastName,
                address1: savedAddress.address,
                address2: savedAddress.apt || '',
                city: savedAddress.city,
                province: savedAddress.stateProvince,
                province_code: savedAddress.stateProvince,
                zip: savedAddress.postalCode,
                country: savedAddress.countryCode,
                country_code: savedAddress.countryCode
            };

            if (!Array.isArray(this.customerAddresses)) {
                this.customerAddresses = [];
            }
            const existingIndex = this.customerAddresses.findIndex((a) => String(a.id) === String(sessionAddress.id));
            if (existingIndex !== -1) {
                this.customerAddresses[existingIndex] = sessionAddress;
            } else {
                this.customerAddresses.push(sessionAddress);
            }

            return savedAddress;
        } catch (error) {
            console.error('[saveAddressToBackend] Error saving address to backend (network/CORS error?):', error);
            return null;
        }
    }

    // Helper function to build formatted address string for comparison
    buildFormattedAddress(addressData) {
        const parts = [
            addressData.address,
            addressData.apartment,
            addressData.city,
            addressData.state,
            addressData.zip_code
        ].filter(part => part && part.trim() !== '');

        return parts.join(' ').replace(/\s+/g, ' ').trim();
    }

    // Identifies an address by the fields Smarty validates, so any later edit no longer matches
    getAddressKey(addressData) {
        return `${addressData.country || ''}|${this.buildFormattedAddress(addressData)}`.toLowerCase();
    }

    // Asks the shopper to pick between what they typed and the validated address.
    // Resolves with one of ADDRESS_CONFIRMATION_OPTIONS. The X and Escape resolve
    // EDIT without a confirm_address event, same as the personalization popup.
    showAddressConfirmationModal(enteredAddress, suggestedAddress) {
        const modal = this.querySelector('.address-confirmation');
        const enteredLine = toSingleAddressLine(enteredAddress);
        const suggestedLine = toSingleAddressLine(suggestedAddress);

        modal.querySelector('.address-confirmation__box').innerHTML =
            renderAddressConfirmationRow('You entered:', renderAddressDiff(enteredLine, suggestedLine, 's'), 'entered')
            + renderAddressConfirmationRow('We suggest:', renderAddressDiff(suggestedLine, enteredLine, 'b'), 'suggested');

        // Analytics must never leave the shopper stuck on "Validating..."
        const track = (send) => {
            try {
                send();
            } catch (error) {
                console.error('[showAddressConfirmationModal] Heap tracking failed:', error);
            }
        };

        return new Promise((resolve) => {
            const finish = (option, isTracked) => {
                modal.removeEventListener('click', onClick);
                document.removeEventListener('keydown', onKeydown);
                modal.hidden = true;
                resolve(option);
                if (isTracked) {
                    track(() => window.heapGlobalNew?.trackConfirmAddress?.({ 'Select Option': option }));
                }
            };

            const onClick = (e) => {
                const actionBtn = e.target.closest('[data-option]');
                if (actionBtn) {
                    finish(actionBtn.dataset.option, true);
                } else if (e.target.closest('.address-confirmation__close')) {
                    finish(ADDRESS_CONFIRMATION_OPTIONS.EDIT, false);
                }
            };

            const onKeydown = (e) => {
                if (e.key === 'Escape') {
                    finish(ADDRESS_CONFIRMATION_OPTIONS.EDIT, false);
                }
            };

            modal.addEventListener('click', onClick);
            document.addEventListener('keydown', onKeydown);
            modal.hidden = false;
            modal.querySelector('.address-confirmation__btn--primary').focus();
            track(() => window.heapGlobalNew?.trackViewAddressConfirmation?.());
        });
    }

    // Writes an accepted suggestion back into the step 1 inputs, so going back from
    // step 2 shows the address that will actually be used.
    fillAddressFields(addressData) {
        ['address', 'apartment', 'city', 'state', 'zip_code'].forEach((fieldName) => {
            const field = this.querySelector(`.delivery-address-form [name="${fieldName}"]`);
            if (field) {
                field.value = addressData[fieldName] || '';
            }
        });
    }

    buildUSAddress(suggestion) {
        return `${suggestion.street_line} ${suggestion.secondary || ''} ${suggestion.city}, ${suggestion.state} ${suggestion.zipcode}`.replace(/\s+/g, ' ').trim();
    }

    buildAddressEntries(suggestion) {
        let secondary = suggestion.secondary;
        if (secondary && suggestion.entries > 1) {
            secondary += ` (${suggestion.entries})`;
        }
        return `${suggestion.street_line} ${secondary || ''} ${suggestion.city} ${suggestion.state} ${suggestion.zipcode}`.replace(/\s+/g, ' ').trim();
    }

    // Shipzip Shipping Rates API Methods
    async prepareShippingRates(addressData) {
        // No longer needed with Shipzip API - rates are fetched directly  
        // Keeping method for backward compatibility
        return true;
    }

    async fetchShippingRates(addressData, maxRetries = 3, delay = 1000) {
        try {
            // Use the global CartShippingRateManager's Shipzip integration
            if (window.cartShippingRateManager) {
                return await window.cartShippingRateManager.shipzipService.fetchRates(addressData);
            } else {
                // Fallback: use shared Shipzip service directly
                return await this.shipzipService.fetchRates(addressData);
            }
        } catch (error) {
            console.error('Error fetching shipping rates:', error);
            return { rates: [], warning_message: null };
        }
    }

    async loadShippingRates(addressData) {
        this.isLoadingShippingRates = true;
        this.updateShippingOptionsUI('loading');

        try {
            // Fetch rates directly from Shipzip API
            // Already ordered for this visitor's ABCD group by fetchRates().
            const { rates, warning_message } = await this.fetchShippingRates(addressData);
            this.shippingRates = rates || [];
            this.shippingWarningMessage = warning_message || null;
            if (this.shippingRates.length == 0) {
                throw new Error('No shipping rates available for the provided address.');
            }
            this.updateShippingOptionsUI('loaded');
            return rates;
        } catch (error) {
            console.error('Error loading shipping rates:', error);
            this.updateShippingOptionsUI('error');
            return [];
        } finally {
            this.isLoadingShippingRates = false;
        }
    }

    updateShippingOptionsUI(state) {
        const shippingOptionsContainer = this.querySelector('.shipping-options');

        if (!shippingOptionsContainer) return;

        switch (state) {
            case 'loading':
                shippingOptionsContainer.innerHTML = `
          <div class="shipping-loading">
            <p>Loading shipping options...</p>
          </div>
        `;
                break;

            case 'loaded':
                this.renderShippingOptions();
                break;

            case 'error':
                shippingOptionsContainer.innerHTML = `
          <div class="shipping-error">
            <p>No shipping options available.</p>
          </div>
        `;
                break;
        }
    }

    renderShippingOptions() {
        const shippingOptionsContainer = this.querySelector('.shipping-options');
        const isSubcriberP1 = window?.customer?.tags.includes("Active product subscription P1");
        const isSubcriberP2OrP3 = window?.customer?.tags.some(tag => tag.includes("Active product subscription P2") || tag.includes("Active product subscription P3"));

        if (!shippingOptionsContainer || !this.shippingRates.length) return;

        // Check if we have existing shipping data to preselect
        const existingShippingName = this.shippingData && this.shippingData.shipping_name ? this.shippingData.shipping_name : null;
        let hasMatchingOption = false;

        const optionsHTML = this.shippingRates.map((rate, index) => {
            const price = rate.price === '0.00' ? 'Free' : `$${rate.price}`;
            const isNonTrackingOption = rate.service_code === "free_below_min"
            const isTrackingOption = rate.service_code === "tracked_free_below_min"

            // Check if this rate matches the existing shipping name
            let isChecked = '';
            if (existingShippingName && rate.name === existingShippingName) {
                isChecked = 'checked';
                hasMatchingOption = true;
            } else if (!existingShippingName && index === 0) {
                // If no existing shipping name, default to first option
                isChecked = 'checked';
            }

            const { text: descText, warning: descWarning } = this.extractWarningFromDescription(rate.description);
            return `
        <label class="shipping-option" for="shipping-rate-${index}">
          <input type="radio" id="shipping-rate-${index}" name="shipping_method" value="${rate.handle}" ${isChecked}>
          <div class="shipping-option-content">
            <div class="shipping-option-info">
                <span class="shipping-name" data-name="${rate.name}">${rate.name}</span>
                ${descText ? `<span class="shipping-date">${this.parseShippingDescription(descText)}</span>` : ''}
                ${isNonTrackingOption && isSubcriberP1 ? "<span class='shopping-option-text-p1'>Everyday Member Perk</span>" : ""}
                ${isTrackingOption && isSubcriberP2OrP3 ? "<span class='shopping-option-text-p1'>Always Member Perk</span>" : ""}
            </div>
            <div class='shipping-price-info'>
                <span class="shipping-price" data-price="${rate.price}">${price}</span>
            </div>
            <span class="shipping-service-code" data-service-code="${rate.service_code || ''}" style="display: none;"></span>
            <span class="shipping-description-data" data-description="${descText || ''}" style="display: none;"></span>
          </div>
          ${descWarning ? `
          <div class="shipping-option-warning">
            <svg class="shipping-warning-icon" width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
              <circle cx="7" cy="7" r="6.5" stroke="#ce3537"/>
              <rect x="6.25" y="3.5" width="1.5" height="5" rx="0.75" fill="#ce3537"/>
              <circle cx="7" cy="10.5" r="0.75" fill="#ce3537"/>
            </svg>
            <span>${descWarning}</span>
          </div>` : ''}
        </label>
      `;
        }).join('');

        // If we had an existing shipping name but didn't find a match, default to first option
        if (existingShippingName && !hasMatchingOption && this.shippingRates.length > 0) {
            const updatedHTML = optionsHTML.replace('name="shipping_method"', 'name="shipping_method" checked');
            shippingOptionsContainer.innerHTML = updatedHTML;
        } else {
            shippingOptionsContainer.innerHTML = optionsHTML;
        }

        const warningContainer = this.querySelector('.shipping-warning-message');
        if (warningContainer) {
            if (this.shippingWarningMessage) {
                warningContainer.innerHTML = `
                    <svg class="shipping-warning-icon" width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <circle cx="7" cy="7" r="6.5" stroke="#ce3537"/>
                      <rect x="6.25" y="3.5" width="1.5" height="5" rx="0.75" fill="#ce3537"/>
                      <circle cx="7" cy="10.5" r="0.75" fill="#ce3537"/>
                    </svg>
                    <span>${this.shippingWarningMessage}</span>
                `;
                warningContainer.style.display = '';
            } else {
                warningContainer.style.display = 'none';
            }
        }
    }

    extractWarningFromDescription(description) {
        if (!description) return { text: '', warning: '' };
        const newlineIdx = description.indexOf('\n');
        if (newlineIdx === -1) return { text: description, warning: '' };
        const text = description.slice(0, newlineIdx).trim();
        const warning = description.slice(newlineIdx + 1).replace(/^[⊗⊙✕×⚠]\s*/, '').trim();
        return { text, warning };
    }

    parseShippingDescription(description) {
        const prefix = "Estimated arrival:";
        if (description.includes(prefix)) {
            const [before, after = ""] = description.split(prefix);
            return `${before}${prefix} <span class="shipping-estimated-date">${after.trim()}</span>`;
        }

        const parenDateRegex = /\((\d{2}\/\d{2}\/\d{4})\)/;
        if (parenDateRegex.test(description)) {
            return description.replace(parenDateRegex, `<span class="shipping-estimated-date">($1)</span>`);
        }

        return description;
    };

    formatDeliveryDate(rate) {
        // You can customize this based on the rate data structure
        // For now, provide a generic message
        if (rate.delivery_date) {
            return rate.delivery_date;
        } else if (rate.min_delivery_date && rate.max_delivery_date) {
            return `${rate.min_delivery_date} - ${rate.max_delivery_date}`;
        } else {
            // Generic delivery estimate based on shipping method name
            // const name = rate.name.toLowerCase();
            // if (name.includes('express') || name.includes('overnight') || name.includes('next day')) {
            //     return '1-2 business days';
            // } else if (name.includes('expedited') || name.includes('priority')) {
            //     return '2-3 business days';
            // } else if (name.includes('standard')) {
            //     return '5-7 business days';
            // } else {
            //     return '5-10 business days';
            // }

            return rate.name;
        }
    }

    setupAddressAutocomplete() {
        const addressInput = this.querySelector('#address');
        const apartmentInput = this.querySelector('#apartment');
        const countrySelect = this.querySelector('#country');

        if (!addressInput) return;

        // Create dropdown container for address suggestions
        this.createDropdown(addressInput, 'address-dropdown');
        this.createDropdown(apartmentInput, 'apartment-dropdown');

        // Address input event listener
        addressInput.addEventListener('input', (e) => {
            this.debounceAddressSearch(e.target.value, this.getSelectedCountry());
        });

        // Apartment input event listener  
        apartmentInput.addEventListener('input', (e) => {
            this.debounceApartmentSearch(e.target.value);
        });

        apartmentInput.addEventListener('focus', (e) => {
            this.debounceApartmentSearch(e.target.value);
        });

        // Country change listener
        countrySelect.addEventListener('change', (e) => {
            this.handleCountryChange(e.target.value);
        });

        // Close dropdowns when clicking outside
        document.addEventListener('click', (e) => {
            const addressDropdown = this.querySelector('#address-dropdown');
            const apartmentDropdown = this.querySelector('#apartment-dropdown');

            // Check if click is outside address input and dropdown
            if (addressInput && addressDropdown) {
                const isClickInsideAddress = addressInput.contains(e.target) || addressDropdown.contains(e.target);
                if (!isClickInsideAddress) {
                    this.hideDropdown('address-dropdown');
                }
            }

            // Check if click is outside apartment input and dropdown
            if (apartmentInput && apartmentDropdown) {
                const isClickInsideApartment = apartmentInput.contains(e.target) || apartmentDropdown.contains(e.target);
                if (!isClickInsideApartment) {
                    this.hideDropdown('apartment-dropdown');
                }
            }
        });

        // Initialize states for the default selected country (US)
        const defaultCountry = countrySelect.value || 'US';
        countrySelect.value = 'US'; // Ensure US is selected
        this.handleCountryChange(defaultCountry);
    }

    createDropdown(inputElement, dropdownId) {
        if (!inputElement) return;

        const dropdown = document.createElement('div');
        dropdown.id = dropdownId;
        dropdown.className = 'address-autocomplete-dropdown';
        dropdown.style.cssText = `
      position: absolute;
      top: 100%;
      left: 0;
      right: 0;
      background: white;
      border: 1px solid #d0d0d0;
      border-top: none;
      border-radius: 0 0 8px 8px;
      max-height: 200px;
      overflow-y: auto;
      z-index: 1000;
      display: none;
    `;

        inputElement.parentElement.style.position = 'relative';
        inputElement.parentElement.appendChild(dropdown);
    }

    debounceAddressSearch(searchQuery, countryCode) {
        clearTimeout(this.debounceTimeout);
        this.debounceTimeout = setTimeout(() => {
            this.searchAddresses(searchQuery, countryCode);
        }, 300);
    }

    debounceApartmentSearch(searchQuery) {
        clearTimeout(this.debounceTimeout);
        this.debounceTimeout = setTimeout(() => {
            this.showApartmentSuggestions(searchQuery);
        }, 300);
    }

    async searchAddresses(searchQuery, countryCode) {
        if (!searchQuery || searchQuery.length < 3) {
            this.hideDropdown('address-dropdown');
            return;
        }

        try {
            const result = await this.fetchAutocomplete(searchQuery, countryCode);

            if (countryCode === 'US') {
                const addresses = result.suggestions || [];
                this.addressSuggestions = addresses.map((addr, index) => ({
                    ...addr,
                    label: this.buildUSAddress(addr),
                    value: index
                }));
            } else {
                const addresses = result.candidates || [];
                this.addressSuggestions = addresses.map((addr, index) => ({
                    ...addr,
                    label: addr.address_text,
                    value: addr.address_id
                }));
            }

            this.showAddressSuggestions();
        } catch (error) {
            console.error('Error searching addresses:', error);
        }
    }

    showAddressSuggestions() {
        const dropdown = this.querySelector('#address-dropdown');
        if (!dropdown || this.addressSuggestions.length === 0) {
            this.hideDropdown('address-dropdown');
            return;
        }

        dropdown.innerHTML = '';

        this.addressSuggestions.slice(0, 5).forEach((suggestion) => {
            const item = document.createElement('div');
            item.className = 'address-suggestion-item';
            item.style.cssText = `
        padding: 12px 16px;
        cursor: pointer;
        border-bottom: 1px solid #f0f0f0;
        font-family: Montserrat, sans-serif;
        font-size: 14px;
      `;
            item.textContent = suggestion.label;

            item.addEventListener('mouseenter', () => {
                item.style.backgroundColor = '#f5f5f5';
            });

            item.addEventListener('mouseleave', () => {
                item.style.backgroundColor = 'white';
            });

            item.addEventListener('click', () => {
                this.selectAddress(suggestion);
            });

            dropdown.appendChild(item);
        });

        dropdown.style.display = 'block';
    }

    showApartmentSuggestions(searchQuery) {
        const dropdown = this.querySelector('#apartment-dropdown');
        if (!dropdown || this.address2Suggestions.length === 0) {
            this.hideDropdown('apartment-dropdown');
            return;
        }

        dropdown.innerHTML = '';

        this.address2Suggestions.forEach((suggestion) => {
            const item = document.createElement('div');
            item.className = 'apartment-suggestion-item';
            item.style.cssText = `
        padding: 12px 16px;
        cursor: pointer;
        border-bottom: 1px solid #f0f0f0;
        font-family: Montserrat, sans-serif;
        font-size: 14px;
      `;
            item.textContent = suggestion.label;

            item.addEventListener('mouseenter', () => {
                item.style.backgroundColor = '#f5f5f5';
            });

            item.addEventListener('mouseleave', () => {
                item.style.backgroundColor = 'white';
            });

            item.addEventListener('click', () => {
                this.selectApartment(suggestion);
            });

            dropdown.appendChild(item);
        });

        dropdown.style.display = 'block';
    }

    async selectAddress(suggestion) {
        const countryCode = this.getSelectedCountry();

        if (countryCode === 'US') {
            // Handle US address selection
            this.fillUSAddress(suggestion);

            // Check for apartment suggestions
            if (suggestion.entries > 1) {
                try {
                    const result = await this.fetchAutocomplete(
                        this.querySelector('#address').value,
                        countryCode,
                        this.buildAddressEntries(suggestion)
                    );

                    const addresses = result.suggestions || [];
                    this.address2Suggestions = addresses.map((addr, index) => ({
                        ...addr,
                        label: addr.secondary,
                        value: index
                    }));
                } catch (error) {
                    console.error('Error fetching apartment suggestions:', error);
                }
            } else {
                this.address2Suggestions = [];
            }
        } else {
            // Handle non-US address selection
            try {
                const address = await this.verifyAddressNonUS(suggestion.value, countryCode);
                if (address) {
                    this.fillNonUSAddress(address, countryCode);
                }
            } catch (error) {
                console.error('Error verifying non-US address:', error);
            }
        }

        this.hideDropdown('address-dropdown');
    }

    selectApartment(suggestion) {
        const apartmentInput = this.querySelector('#apartment');
        if (apartmentInput) {
            apartmentInput.value = suggestion.label;
        }
        this.hideDropdown('apartment-dropdown');
    }

    fillUSAddress(suggestion) {
        const addressInput = this.querySelector('#address');
        const cityInput = this.querySelector('#city');
        const stateSelect = this.querySelector('#state');
        const zipInput = this.querySelector('#zip-code');

        if (addressInput) addressInput.value = suggestion.street_line;
        if (cityInput) cityInput.value = suggestion.city;
        if (stateSelect) stateSelect.value = suggestion.state;
        if (zipInput) zipInput.value = suggestion.zipcode;
    }

    fillNonUSAddress(address, countryCode) {
        const addressInput = this.querySelector('#address');
        const cityInput = this.querySelector('#city');
        const stateSelect = this.querySelector('#state');
        const zipInput = this.querySelector('#zip-code');

        if (addressInput) addressInput.value = address.street;
        if (cityInput) cityInput.value = address.locality;
        if (zipInput) zipInput.value = address.postal_code;

        if (stateSelect) {
            let stateValue = countryCode === 'JP' ?
                address.administrative_area_long :
                address.administrative_area_short;

            // Normalize Japanese prefecture from Kanji to Romaji
            if (countryCode === 'JP' && stateValue) {
                stateValue = this.normalizeJapanesePrefecture(stateValue, countryCode);
            }

            stateSelect.value = stateValue;
        }
    }

    hideDropdown(dropdownId) {
        const dropdown = this.querySelector(`#${dropdownId}`);
        if (dropdown) {
            dropdown.style.display = 'none';
        }
    }

    getSelectedCountry() {
        const countrySelect = this.querySelector('#country');
        if (countrySelect) {
            // Ensure US is the default if no value is set
            if (!countrySelect.value) {
                countrySelect.value = 'US';
            }
            return countrySelect.value;
        }
        return 'US'; // Default fallback
    }

    handleCountryChange(countryCode) {
        const stateSelect = this.querySelector('#state');
        const stateFormField = stateSelect?.closest('.form-field');
        if (!stateSelect) return;

        // Clear all address fields only when user manually changes country (not during initialization)
        if (!this.isInitializing) {
            const addressInput = this.querySelector('#address');
            const apartmentInput = this.querySelector('#apartment');
            const cityInput = this.querySelector('#city');
            const zipInput = this.querySelector('#zip-code');

            if (addressInput) addressInput.value = '';
            if (apartmentInput) apartmentInput.value = '';
            if (cityInput) cityInput.value = '';
            if (zipInput) zipInput.value = '';
        }

        // Countries that don't use state/province field at all - completely hide it
        const countriesWithoutStates = ['GB', 'UK', 'DE', 'FR', 'CH', 'IT', 'ES', 'NL', 'BE', 'SE', 'NO', 'DK', 'FI'];

        // Hide state field for countries that don't use it
        if (countriesWithoutStates.includes(countryCode)) {
            stateSelect.value = ''; // Clear value
            stateSelect.removeAttribute('required'); // Remove required attribute to prevent validation error
            if (stateFormField) {
                stateFormField.style.display = 'none';
                stateFormField.classList.add('hidden');
            }
            // Clear address suggestions when country changes
            this.addressSuggestions = [];
            this.address2Suggestions = [];
            this.hideDropdown('address-dropdown');
            this.hideDropdown('apartment-dropdown');
            return;
        } else {
            // Show state field for countries that use it
            stateSelect.setAttribute('required', 'required'); // Add required attribute back
            if (stateFormField) {
                stateFormField.style.display = '';
                stateFormField.classList.remove('hidden');
            }
        }

        // Clear current state options
        stateSelect.innerHTML = '<option value="">Select State</option>';

        // Get states for the selected country
        const states = this.countryStates[countryCode];

        if (states && states.length > 0) {
            // Update label and placeholder based on country
            const stateLabel = this.querySelector('label[for="state"]');
            let labelText = 'State';
            let placeholderText = 'Select State';

            switch (countryCode) {
                case 'CA':
                    labelText = 'Province';
                    placeholderText = 'Select Province';
                    break;
                case 'JP':
                    labelText = 'Prefecture';
                    placeholderText = 'Select Prefecture';
                    break;
                case 'IE':
                    labelText = 'County';
                    placeholderText = 'Select County';
                    break;
                case 'AU':
                case 'US':
                default:
                    labelText = 'State';
                    placeholderText = 'Select State';
                    break;
            }

            if (stateLabel) {
                stateLabel.textContent = labelText;
            }

            // Set placeholder option
            stateSelect.innerHTML = `<option value="">${placeholderText}</option>`;

            // Add state options
            states.forEach(state => {
                const option = document.createElement('option');
                option.value = state.value;
                option.textContent = state.label;
                stateSelect.appendChild(option);
            });
        } else {
            // For countries without predefined states, make it a text input
            const stateLabel = this.querySelector('label[for="state"]');
            if (stateLabel) {
                stateLabel.textContent = 'State/Province/Region';
            }
        }

        // Clear address suggestions when country changes
        this.addressSuggestions = [];
        this.address2Suggestions = [];
        this.hideDropdown('address-dropdown');
        this.hideDropdown('apartment-dropdown');
    }

    /**
     * Get licensors from all "send to me" cart items
     */
    async getSTMProductLicensors() {
        try {
            // Fetch cart data if not available
            if (!window.cartItems || !Array.isArray(window.cartItems)) {
                const cartResponse = await fetch('/cart.js');
                const cartData = await cartResponse.json();
                window.cartItems = cartData.items || [];
            }

            // Find all "send to me" items (items without _surprise property)
            const stmItems = window.cartItems.filter(item => {
                const properties = item.properties || {};
                // Item is STM if it doesn't have _surprise and either has _hasSAS or is a regular item
                if (properties._surprise || (properties._hasSAS && properties._address1)) return false;
                return true;
            });

            if (stmItems.length === 0) {
                // console.log('No STM items found in cart');
                return [];
            }

            // console.log(`Found ${stmItems.length} STM items in cart`);

            // Get licensors from all STM items using data-licensor attribute from cart DOM
            const licensors = [];
            for (const item of stmItems) {
                try {
                    // Find cart item element by key and get licensor from data attribute
                    const cartItemElement = document.querySelector(`[data-key="${item.key}"]`);
                    const licensor = cartItemElement?.getAttribute('data-licensor') || '';

                    // Only add valid licensors
                    if (licensor && licensor !== '' && licensor !== 'None') {
                        licensors.push(licensor);
                        // console.log(`Product ${item.handle} has licensor:`, licensor);
                    }
                } catch (error) {
                    console.error(`Error getting licensor for item ${item.key}:`, error);
                }
            }

            // Return unique licensors
            return [...new Set(licensors)];
        } catch (error) {
            console.error('Error getting STM product licensors:', error);
            return [];
        }
    }

    /**
     * Get allowed countries based on multiple licensors and combine results
     */
    async getAllowedCountriesForLicensors(licensors) {
        if (!licensors || licensors.length === 0) {
            // console.log('No valid licensors, skipping country restrictions');
            return null;
        }

        try {
            const allAllowedCountries = [];

            // Fetch allowed countries for each licensor
            for (const licensor of licensors) {
                try {
                    const response = await fetch(
                        `https://18bd2kgvq9.execute-api.us-west-2.amazonaws.com/prod/floo/api/Restrictions/GetAllowedConnections?licensorTags=${encodeURIComponent(licensor)}`,
                        {
                            method: 'POST',
                            headers: {
                                Accept: 'application/json',
                                'Content-Type': 'application/json',
                                'x-api-key': 'yWKY6dCfrk5ljIfeVKMRT629pb2MGbE25XjKWgPc'
                            },
                        }
                    );

                    const data = await response.json();
                    // console.log(`Allowed countries for licensor "${licensor}":`, data.countries);
                    
                    if (data.countries && Array.isArray(data.countries)) {
                        allAllowedCountries.push(...data.countries);
                    }
                } catch (error) {
                    console.error(`Error fetching allowed countries for licensor "${licensor}":`, error);
                }
            }

            // Return unique countries (combine all allowed countries from all licensors)
            const uniqueCountries = [...new Set(allAllowedCountries)];
            // console.log('Combined allowed countries from all products:', uniqueCountries);
            
            return uniqueCountries.length > 0 ? uniqueCountries : null;
        } catch (error) {
            console.error('Error fetching allowed countries:', error);
            return null;
        }
    }

    /**
     * Populate country dropdown with allowed countries
     */
    resetCountryDropdown() {
        const countrySelect = this.querySelector('#country');
        if (!countrySelect) return;

        // Store current country before modifying dropdown
        const currentCountry = countrySelect.value;

        // Define all available countries with their codes using the class property
        const allCountries = Object.entries(this.countryNames).map(([code, name]) => ({ code, name }));

        // Clear existing options
        countrySelect.innerHTML = '';

        // Add all countries
        allCountries.forEach(country => {
            const option = document.createElement('option');
            option.value = country.code;
            option.textContent = country.name;
            countrySelect.appendChild(option);
        });

        // Restore current country if it exists in the list
        if (currentCountry && allCountries.some(c => c.code === currentCountry)) {
            countrySelect.value = currentCountry;
        } else {
            // Default to US
            countrySelect.value = 'US';
        }
    }

    populateCountryDropdown(allowedCountries) {
        const countrySelect = this.querySelector('#country');
        if (!countrySelect) return;

        // Store current country before modifying dropdown
        const currentCountry = countrySelect.value;

        // Define all available countries with their codes using the class property
        const allCountries = Object.entries(this.countryNames).map(([code, name]) => ({ code, name }));

        // If no allowed countries restriction, show all countries
        if (!allowedCountries || !Array.isArray(allowedCountries) || allowedCountries.length === 0) {
            return; // Keep default countries in template
        }

        // Filter countries based on allowed list (case-insensitive match)
        const allowedCountriesUpper = allowedCountries.map(c => c.toUpperCase());
        const filteredCountries = allCountries.filter(country =>
            allowedCountriesUpper.includes(country.code.toUpperCase())
        );

        // Clear existing options
        countrySelect.innerHTML = '';

        // Add filtered countries
        if (filteredCountries.length > 0) {
            filteredCountries.forEach(country => {
                const option = document.createElement('option');
                option.value = country.code;
                option.textContent = country.name;
                countrySelect.appendChild(option);
            });

            // Check if current country is in the allowed list
            const currentCountryAllowed = filteredCountries.some(c => c.code === currentCountry);

            if (currentCountryAllowed && this.isEditing) {
                // If editing and current country is allowed, keep it selected without triggering change
                countrySelect.value = currentCountry;
            } else if (currentCountryAllowed) {
                // Current country is allowed, set it but trigger change for non-edit mode
                countrySelect.value = currentCountry;
                this.handleCountryChange(currentCountry);
            } else {
                // Current country not allowed, set first country as selected
                countrySelect.value = filteredCountries[0].code;
                this.handleCountryChange(filteredCountries[0].code);
            }
        } else {
            // If no matches, add a default option
            const option = document.createElement('option');
            option.value = 'US';
            option.textContent = 'United States';
            countrySelect.appendChild(option);
            countrySelect.value = 'US';
            this.handleCountryChange('US');
        }
    }

    async open(isEdit = false, redirectToCheckout = false) {
        this.isEditing = isEdit;
        this.redirectToCheckout = redirectToCheckout; // Store flag for checkout redirect
        this.confirmedAddressKey = null; // Every add/edit session confirms its own address
        const overlay = this.querySelector('.delivery-address-overlay');
        const container = this.querySelector('.delivery-address-container');

        this.isOpen = true;
        overlay.style.display = 'flex';

        // Prevent background scrolling - store original overflow to restore later
        this.originalOverflow = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        document.body.classList.add('delivery-modal-open');

        // Get allowed countries once and cache it for reuse
        try {
            const licensors = await this.getSTMProductLicensors();
            if (licensors && licensors.length > 0) {
                this.allowedCountries = await this.getAllowedCountriesForLicensors(licensors);
            } else {
                this.allowedCountries = null;
            }
        } catch (error) {
            console.error('Error fetching allowed countries:', error);
            this.allowedCountries = null;
        }

        // Only reset form if it's not an edit operation
        if (!isEdit) {
            if (!this.customerAddresses || this.customerAddresses.length < 1) {
                document.querySelectorAll('.delivery-address-go-back-btn, .delivery-address-back-btn svg, .delivery-address-cancel-btn').forEach(btn => {
                    btn.classList.add('hidden');
                });
            }
            this.resetForm();

            // Apply country restrictions using cached allowed countries
            if (this.allowedCountries && this.allowedCountries.length > 0) {
                this.populateCountryDropdown(this.allowedCountries);
            } else {
                // No restrictions, reset to show all countries
                this.resetCountryDropdown();
            }
        } else {
            document.querySelectorAll('.delivery-address-go-back-btn, .delivery-address-back-btn svg, .delivery-address-cancel-btn').forEach(btn => {
                btn.classList.add('hidden');
            });

            const continueButton = document.querySelector('.delivery-address-continue-btn');
            if (continueButton) {
                continueButton.textContent = 'Update Delivery Address';
            }

            // Apply country restrictions using cached allowed countries
            if (this.allowedCountries && this.allowedCountries.length > 0) {
                this.populateCountryDropdown(this.allowedCountries);
            } else {
                // No restrictions, reset to show all countries
                this.resetCountryDropdown();
            }
        }

        // Trigger animation
        requestAnimationFrame(() => {
            overlay.classList.add('active');
            container.classList.add('active');
        });
    }

    openWithError(errorInfo) {
        // Open the form in edit mode
        this.open(true, false);

        // Only force step 1 if address form is already filled
        const hasExistingAddress = window.stm_init_data && (window.stm_init_data._first_name || window.stm_init_data._address1);

        if (hasExistingAddress) {
            // Address already filled, go to step 1 to show the error
            this.currentStep = 1;
            this.goToStep(1);

            // Show back/cancel buttons when customer has addresses
            if (this.customerAddresses && this.customerAddresses.length > 0) {
                document.querySelectorAll('.delivery-address-go-back-btn, .delivery-address-back-btn svg, .delivery-address-cancel-btn').forEach(btn => {
                    btn.classList.remove('hidden');
                });
            }
        }
        // If no existing address, let normal flow handle which step to show
    }

    close() {
        const overlay = this.querySelector('.delivery-address-overlay');
        const container = this.querySelector('.delivery-address-container');
        const errorContainer = this.querySelector('.delivery-address-error');

        overlay.classList.remove('active');
        container.classList.remove('active');

        // Hide error message
        if (errorContainer) {
            errorContainer.style.display = 'none';
        }

        setTimeout(() => {
            overlay.style.display = 'none';

            // Restore background scrolling
            document.body.style.overflow = this.originalOverflow || '';
            document.body.classList.remove('delivery-modal-open');

            this.isOpen = false;
            this.resetForm();
        }, 300);
    }

    resetForm() {
        const form = this.querySelector('.delivery-address-form');
        form.reset();
        this.currentStep = 1;
        this.addressData = {};
        this.shippingData = {};
        this.addressSuggestions = [];
        this.address2Suggestions = [];
        this.clearValidationErrors();
        this.hideDropdown('address-dropdown');
        this.hideDropdown('apartment-dropdown');

        // Reset country to US and ensure state field is visible
        const countrySelect = this.querySelector('#country');
        if (countrySelect) {
            countrySelect.value = 'US';
            this.isInitializing = true;
            this.handleCountryChange('US');
            this.isInitializing = false;
        }

        if (this.customerAddresses && this.customerAddresses.length > 0) {
            this.goToStep(0);
        } else {
            this.goToStep(1);
        }
    }

    async goToStep(stepNumber) {
        this.currentStep = stepNumber;

        // Hide error message when changing steps
        const errorContainer = this.querySelector('.delivery-address-error');
        if (errorContainer) {
            errorContainer.style.display = 'none';
        }

        // Update title based on step
        const title = this.querySelector('.delivery-address-title');
        if (stepNumber === 0) {
            title.textContent = 'Choose Shipping Address';
        } else if (stepNumber === 1) {
            title.textContent = 'Where is the Card Going?';
        } else if (stepNumber === 2) {
            title.textContent = 'When do you want to get it there?';
        }

        if (!this.isEditing) {
            document.querySelectorAll('.delivery-address-go-back-btn, .delivery-address-back-btn svg, .delivery-address-cancel-btn').forEach(btn => {
                btn.classList.remove('hidden');
            });
        }

        // Show/hide steps
        const step0 = this.querySelector('.step-0');
        const step1 = this.querySelector('.step-1');
        const step2 = this.querySelector('.step-2');

        if (stepNumber === 0) {
            if (step0) step0.style.display = 'flex';
            if (step1) step1.style.display = 'none';
            if (step2) step2.style.display = 'none';
            await this.renderSavedAddresses();
        } else if (stepNumber === 1) {
            if (step0) step0.style.display = 'none';
            if (step1) step1.style.display = 'flex';
            if (step2) step2.style.display = 'none';

            // Show/hide back to addresses button based on whether user has saved addresses
            const backToAddressesBtn = this.querySelector('.delivery-address-back-to-addresses-btn');
            if (backToAddressesBtn) {
                backToAddressesBtn.style.display =
                    (this.customerAddresses && this.customerAddresses.length > 0) ? 'block' : 'none';
            }

            if (!this.customerAddresses || this.customerAddresses.length < 1) {
                document.querySelectorAll('.delivery-address-go-back-btn, .delivery-address-back-btn svg, .delivery-address-cancel-btn').forEach(btn => {
                    btn.classList.add('hidden');
                });
            }

            // Clear form if coming from address selection with new address
            if (this.comingFromAddressSelection) {
                this.clearAddressForm();
                this.comingFromAddressSelection = false;
            }
        } else if (stepNumber === 2) {
            if (step0) step0.style.display = 'none';
            if (step1) step1.style.display = 'none';
            if (step2) step2.style.display = 'flex';

            // Load shipping rates when moving to step 2
            if (this.addressData && Object.keys(this.addressData).length > 0) {
                this.loadShippingRates(this.addressData);
            }
        }
    }

    async renderSavedAddresses() {
        const container = this.querySelector('.saved-addresses');
        if (!container || !this.customerAddresses || this.customerAddresses.length === 0) {
            return;
        }

        // Use cached allowed countries from open() method
        const allowedCountries = this.allowedCountries;

        // Drop empty/placeholder addresses (missing address1/city, or literal "null"/"undefined" values)
        const isEmptyValue = (value) => value === null || value === undefined || value === '' || value === 'null' || value === 'undefined';
        let filteredAddresses = this.customerAddresses.filter(address => {
            return !isEmptyValue(address.address1) && !isEmptyValue(address.city);
        });

        // Filter addresses by allowed countries if restrictions exist
        if (allowedCountries && allowedCountries.length > 0) {
            const allowedCountriesUpper = allowedCountries.map(c => c.toUpperCase());
            filteredAddresses = filteredAddresses.filter(address => {
                const countryCode = address.country_code || address.country;
                return allowedCountriesUpper.includes(countryCode.toUpperCase());
            });
        }

        // Keep the default address pinned at the top; sort the rest alphabetically by first name, then last name
        const defaultAddressId = window.customerDefaultAddressId;
        const defaultAddresses = [];
        const otherAddresses = [];
        filteredAddresses.forEach(address => {
            if (defaultAddressId && String(address.id) === String(defaultAddressId)) {
                defaultAddresses.push(address);
            } else {
                otherAddresses.push(address);
            }
        });
        otherAddresses.sort((a, b) => {
            const firstNameCompare = (a.first_name || '').localeCompare(b.first_name || '');
            if (firstNameCompare !== 0) return firstNameCompare;
            return (a.last_name || '').localeCompare(b.last_name || '');
        });
        filteredAddresses = [...defaultAddresses, ...otherAddresses];

        // If no addresses pass the filter, show message
        if (filteredAddresses.length === 0) {
            container.innerHTML = `<div style="padding: 20px; text-align: center; color: #666;">
                No saved addresses are available for the items in your cart.
            </div>`;
            // Disable use selected button
            const useBtn = this.querySelector('.delivery-address-use-selected-btn');
            if (useBtn) useBtn.disabled = true;
            return;
        }

        container.innerHTML = filteredAddresses.map((address, index) => {
            // Find original index for data-address-index
            const originalIndex = this.customerAddresses.indexOf(address);
            const isSelected = this.selectedAddressId && String(address.id) === String(this.selectedAddressId);
            return `
            <div class="address-option${isSelected ? ' selected' : ''}" data-address-index="${originalIndex}">
                <input type="radio" name="selected_address" value="${originalIndex}" id="address_${originalIndex}" ${isSelected ? 'checked' : ''}>
                <div class="address-info">
                    ${defaultAddressId && String(address.id) === String(defaultAddressId) ? '<div>Default Shipping Address</div>' : ''}
                    <div class="address-name">${address.first_name || ''} ${address.last_name || ''}</div>
                    <div class="address-details">
                        ${address.address1}${address.address2 ? ', ' + address.address2 : ''}<br>
                        ${address.city}, ${address.province || address.state} ${address.zip}<br>
                        ${address.country}
                    </div>
                </div>
            </div>
            `;
        }).join('');

        // Preserve the "Use Selected Address" enabled state across re-renders if a prior selection still exists
        const preservedSelectionBtn = this.querySelector('.delivery-address-use-selected-btn');
        if (preservedSelectionBtn && this.selectedAddressId && filteredAddresses.some(address => String(address.id) === String(this.selectedAddressId))) {
            preservedSelectionBtn.disabled = false;
        }

        // Add click handlers for address options
        container.querySelectorAll('.address-option').forEach(option => {
            option.addEventListener('click', (e) => {
                // Unselect all
                container.querySelectorAll('.address-option').forEach(opt => opt.classList.remove('selected'));
                container.querySelectorAll('input[type="radio"]').forEach(radio => radio.checked = false);

                // Select clicked option
                option.classList.add('selected');
                option.querySelector('input[type="radio"]').checked = true;

                // Remember the selection so it survives re-renders when navigating back to this step
                const originalIndex = parseInt(option.dataset.addressIndex, 10);
                const address = this.customerAddresses[originalIndex];
                this.selectedAddressId = address ? address.id : null;

                // Enable use selected button
                const useBtn = this.querySelector('.delivery-address-use-selected-btn');
                if (useBtn) useBtn.disabled = false;
            });
        });
    }

    handleUseSelectedAddress() {
        const selectedRadio = this.querySelector('input[name="selected_address"]:checked');
        if (!selectedRadio) return;

        const addressIndex = parseInt(selectedRadio.value);
        const selectedAddress = this.customerAddresses[addressIndex];

        // Pre-fill the form with selected address
        this.prefillAddressForm(selectedAddress);

        // Go to step 1 (address form) so user can review/modify the address
        this.goToStep(2);
    }

    handleEnterNewAddress() {
        this.comingFromAddressSelection = true;
        this.goToStep(1);
    }

    prefillAddressForm(address) {
        const form = this.querySelector('.delivery-address-form');
        if (!form) return;

        // Map address fields
        const fieldMapping = {
            'first_name': address.first_name,
            'last_name': address.last_name,
            'address': address.address1,
            'address2': address.address2 || '',
            "apartment": address.address2 || '',
            'city': address.city,
            'state': address.province_code || address.province,
            'zip_code': address.zip,
            'country': address.country_code
        };

        // Fill form fields (except country and state - handle those separately)
        Object.entries(fieldMapping).forEach(([fieldName, value]) => {
            if (fieldName === 'country' || fieldName === 'state') return; // Skip these for now

            const field = form.querySelector(`[name="${fieldName}"]`);
            if (field && value) {
                field.value = value;
            }
        });

        // Handle country and state separately to ensure proper state dropdown update
        const countryField = form.querySelector('[name="country"]');
        const stateField = form.querySelector('[name="state"]');

        if (countryField && fieldMapping.country) {
            // Set flag to prevent clearing fields during initialization
            this.isInitializing = true;

            // Set country value
            countryField.value = fieldMapping.country;

            // Trigger country change to update states dropdown
            this.handleCountryChange(fieldMapping.country);

            // Reset flag
            this.isInitializing = false;

            // Set state value after states are loaded
            if (stateField && fieldMapping.state) {
                // Normalize Japanese prefecture if needed
                const normalizedState = this.normalizeJapanesePrefecture(fieldMapping.state, fieldMapping.country);

                // Use setTimeout to ensure states are loaded first
                setTimeout(() => {
                    stateField.value = normalizedState;
                }, 50);
            }
        }

        // Store address data (with normalized state)
        if (fieldMapping.state && fieldMapping.country) {
            fieldMapping.state = this.normalizeJapanesePrefecture(fieldMapping.state, fieldMapping.country);
        }
        this.addressData = fieldMapping;
    }

    clearAddressForm() {
        const form = this.querySelector('.delivery-address-form');
        if (!form) return;

        const fields = form.querySelectorAll('input, select');
        fields.forEach(field => {
            if (field.type !== 'hidden' && field.name !== 'country') {
                field.value = '';
            }
        });

        this.addressData = {};
    }

    normalizeJapanesePrefecture(state, countryCode) {
        // If not Japan, return as is
        if (countryCode !== 'JP') {
            return state;
        }

        // If it's already in Romaji (exists in our state list), return it
        const jpStates = this.countryStates['JP'];
        if (jpStates && jpStates.some(s => s.value === state)) {
            return state;
        }

        // Try to convert from Kanji to Romaji
        if (this.japanPrefectureMap[state]) {
            return this.japanPrefectureMap[state];
        }

        // Return as is if no mapping found
        return state;
    }

    async handleStep1Continue() {
        const formData = new FormData(this.querySelector('.delivery-address-form'));
        this.addressData = Object.fromEntries(formData.entries());

        // Normalize Japanese prefecture if needed
        if (this.addressData.country === 'JP' && this.addressData.state) {
            this.addressData.state = this.normalizeJapanesePrefecture(this.addressData.state, this.addressData.country);
        }

        // Clear previous errors
        this.clearValidationErrors();

        // Basic validation for step 1
        let requiredFields = ['first_name', 'last_name', 'address', 'city', 'zip_code', 'country'];

        // Countries that use state/province/county field
        const countriesWithStates = ['US', 'CA', 'AU', 'IE', 'JP'];

        // Add state to required fields only for countries that use it
        if (countriesWithStates.includes(this.addressData.country)) {
            requiredFields.push('state');
        }

        const fieldLabels = {
            'first_name': 'First name',
            'last_name': 'Last name',
            'address': 'Address',
            'city': 'City',
            'state': 'State',
            'zip_code': 'Zip code',
            'country': 'Country'
        };

        let hasErrors = false;

        // Check required fields
        for (const field of requiredFields) {
            if (!this.addressData[field] || !this.addressData[field].trim()) {
                this.showFieldError(field, `${fieldLabels[field]} is required`);
                hasErrors = true;
            }
        }

        // Validate postal code format
        if (this.addressData.zip_code && this.addressData.zip_code.trim()) {
            const postalCodeValidation = this.validatePostalCode(this.addressData.zip_code, this.addressData.country);
            if (!postalCodeValidation.isValid) {
                this.showFieldError('zip_code', `Invalid postal code format. ${postalCodeValidation.message}`);
                hasErrors = true;
            }
        }

        if (hasErrors) {
            return; // Stop here if validation fails
        }

        // Validate address using enhanced Smarty API validation
        try {
            const continueBtn = this.querySelector('.delivery-address-continue-btn');
            const originalText = continueBtn.textContent;
            continueBtn.textContent = 'Validating...';
            continueBtn.disabled = true;

            // An address the shopper already settled in the confirmation modal is not asked about again
            const validationResult = this.confirmedAddressKey === this.getAddressKey(this.addressData)
                ? { isValid: true, addressData: this.addressData }
                : await this.validateAndConfirmAddress();

            if (validationResult.requiresConfirmation) {
                const choice = await this.showAddressConfirmationModal(this.addressData, validationResult.suggestedAddress);

                if (choice === ADDRESS_CONFIRMATION_OPTIONS.EDIT) {
                    continueBtn.textContent = originalText;
                    continueBtn.disabled = false;
                    this.querySelector('.delivery-address-form [name="address"]')?.focus();
                    return;
                }

                if (choice === ADDRESS_CONFIRMATION_OPTIONS.USE_SUGGESTED) {
                    this.addressData = validationResult.suggestedAddress;
                    this.fillAddressFields(this.addressData);
                }
                this.confirmedAddressKey = this.getAddressKey(this.addressData);
            } else if (!validationResult.isValid) {
                // Show error and ask for confirmation
                const confirmContinue = confirm(`${validationResult.error} Do you want to continue anyway?`);
                if (!confirmContinue) {
                    continueBtn.textContent = originalText;
                    continueBtn.disabled = false;
                    return;
                }
            } else {
                // Address is valid, use the validated version if available
                if (validationResult.addressData) {
                    this.addressData = validationResult.addressData;
                }
                // console.log('Address validated successfully:', this.addressData);
            }

            continueBtn.textContent = originalText;
            continueBtn.disabled = false;

            // Persist the address to Shopify's customer address book (no-ops for guests)
            await this.saveAddressToBackend(this.addressData);

            // Move to step 2
            this.goToStep(2);

        } catch (error) {
            console.error('[handleStep1Continue] Error validating address (save was NOT attempted):', error);
            const continueBtn = this.querySelector('.delivery-address-continue-btn');
            continueBtn.textContent = 'Add Delivery Address';
            continueBtn.disabled = false;

            const confirmContinue = confirm('Unable to validate address. Do you want to continue anyway?');
            if (confirmContinue) {
                this.goToStep(2);
            }
        }
    }

    async handleFinalSubmit() {
        // Collect shipping method data
        const form = this.querySelector('.delivery-address-form');
        const shippingFormData = new FormData(form);
        const selectedShippingMethod = form.querySelector('input[name="shipping_method"]:checked');
        this.shippingData = {
            shipping_method: shippingFormData.get('shipping_method'),
            shipping_price: selectedShippingMethod.parentElement.querySelector('.shipping-price').dataset.price,
            shipping_name: selectedShippingMethod.parentElement.querySelector('.shipping-name').dataset.name,
            service_code: selectedShippingMethod.parentElement.querySelector('.shipping-service-code').dataset.serviceCode,
            shipping_description: selectedShippingMethod.parentElement.querySelector('.shipping-description-data').dataset.description
        };

        // console.log('Final address data:', this.addressData);
        // console.log('Final shipping data:', this.shippingData);

        // Update cart with both address and shipping data
        await this.updateCartWithAddressAndShipping(this.addressData, this.shippingData);

        // Always ensure shipping persistence after updating cart
        if (window.shippingPersistenceService) {
            try {
                const persistenceResult = await window.shippingPersistenceService.ensureShippingPersistence();

                if (!persistenceResult) {
                    window.heapGlobal?.trackEvent(
                        "shipping_persistence_failed"
                    );

                    // Retry storing shipping data
                    if (this.shippingData && this.shippingData.shipping_name) {
                        try {
                            await window.shippingPersistenceService.storeShippingData(
                                this.shippingData.shipping_name,
                                this.shippingData.shipping_price,
                                this.shippingData.service_code || '',
                                this.shippingData.shipping_description || ''
                            );
                        } catch (retryError) {
                            console.error('Retry store shipping data failed:', retryError);
                        }
                    }
                }
            } catch (error) {
                console.error('Error ensuring shipping persistence:', error);

                window.heapGlobal?.trackEvent(
                    "shipping_persistence_error"
                );

                // Attempt fallback store
                if (this.shippingData && this.shippingData.shipping_name) {
                    try {
                        await window.shippingPersistenceService.storeShippingData(
                            this.shippingData.shipping_name,
                            this.shippingData.shipping_price,
                            this.shippingData.service_code || '',
                            this.shippingData.shipping_description || ''
                        );
                    } catch (fallbackError) {
                        console.error('Fallback store shipping data failed:', fallbackError);
                    }
                }
            }
        }

        this.close();

        // If this was triggered by checkout button, redirect to checkout
        if (this.redirectToCheckout) {
            window.heapGlobal?.trackEvent(
                "Started Checkout",
                {
                    Page: window.getPageForAnalytics(),
                    Category: '',
                    Type: 'Labeled event',
                    Version: '',
                }
            );

            setTimeout(() => {
                window.location.href = '/checkout';
            }, 1000);
            return;
        }

        //remove old heap event
        // window.heapGlobal?.trackEvent(
        //     "edit_address_save",
        //     {
        //         Page: window.getPageForAnalytics(),
        //         Category: 'Personalization',
        //         Type: 'Custom event',
        //         Version: window._lovepop_version,
        //         Source: window.getPageForAnalytics(),
        //         "Select Option": 'Send to me'
        //     }
        // );

        //remove old heap event
        // window.heapGlobal?.trackEvent(
        //     "edit_shipping_method_save",
        //     {
        //         Page: window.getPageForAnalytics(),
        //         Category: 'Personalization',
        //         Type: 'Custom event',
        //         Version: window._lovepop_version,
        //         Source: window.getPageForAnalytics(),
        //         "Select Option": this.shippingData._shipping_name
        //     }
        // );

        //trigger new heap event
        if (this.isEditing) {
            window.heapGlobalNew?.trackEditShippingMethodSave?.();
        } else {
            window.heapGlobalNew?.trackSendToMeSelectShippingMethod?.({
                'Select Option': this.shippingData?.shipping_name || ''
            });
        }

        // Dispatch custom event with complete data
        this.dispatchEvent(new CustomEvent('deliveryDataAdded', {
            detail: {
                address: this.addressData,
                shipping: this.shippingData
            },
            bubbles: true
        }));
    }

    // Helper method to show field error
    showFieldError(fieldName, message) {
        const input = this.querySelector(`[name="${fieldName}"]`);
        if (!input) return;

        const formField = input.closest('.form-field');
        if (!formField) return;

        // Add error class to form field
        formField.classList.add('has-error');

        // Remove existing error message
        const existingError = formField.querySelector('.field-error');
        if (existingError) {
            existingError.remove();
        }

        // Add error message
        const errorElement = document.createElement('div');
        errorElement.className = 'field-error';
        errorElement.textContent = message;

        // Insert after the input
        input.parentNode.insertBefore(errorElement, input.nextSibling);
    }

    // Helper method to clear all validation errors
    clearValidationErrors() {
        // Remove error classes from form fields
        this.querySelectorAll('.form-field.has-error').forEach(field => {
            field.classList.remove('has-error');
        });

        // Remove error messages
        this.querySelectorAll('.field-error').forEach(error => {
            error.remove();
        });
    }

    validatePostalCode(postalCode, countryCode) {
        const usPostalCodeRegex = /^\d{5}(?:[-\s]\d{4})?$/;
        const caPostalCodeRegex = /^[A-Za-z]\d[A-Za-z][ -]?\d[A-Za-z]\d$/;

        switch (countryCode) {
            case 'US':
                if (usPostalCodeRegex.test(postalCode)) {
                    return { isValid: true };
                } else {
                    return { isValid: false, message: 'Example: 12345 or 12345-6789' };
                }
            case 'CA':
                if (caPostalCodeRegex.test(postalCode)) {
                    return { isValid: true };
                } else {
                    return { isValid: false, message: 'Example: A1B 2D3, A1B-2D3, or A1B2D3' };
                }
            default:
                // For other countries, accept any non-empty postal code
                if (postalCode && postalCode.trim().length > 0) {
                    return { isValid: true };
                } else {
                    return { isValid: false, message: 'Postal code is required' };
                }
        }
    }

    initializeWithData(data) {
        if (!data) return;

        // Fill form fields with existing data
        const fields = {
            'first_name': data._first_name,
            'last_name': data._last_name,
            'address': data._address1,
            'apartment': data._address2,
            'city': data._city,
            'state': data._state,
            'zip_code': data._zip_code,
            'country': data._country
        };

        Object.entries(fields).forEach(([fieldName, value]) => {
            if (value) {
                const input = this.querySelector(`[name="${fieldName}"]`);
                if (input) {
                    input.value = value;

                    // Trigger country change to populate states if country is set
                    if (fieldName === 'country') {
                        this.isInitializing = true;
                        this.handleCountryChange(value);
                        this.isInitializing = false;
                        // Wait a bit for states to populate, then set state value
                        setTimeout(() => {
                            const stateInput = this.querySelector('[name="state"]');
                            if (stateInput && data._state) {
                                // Normalize Japanese prefecture if needed
                                const normalizedState = this.normalizeJapanesePrefecture(data._state, data._country);
                                stateInput.value = normalizedState;
                            }
                        }, 100);
                    }
                }
            }
        });

        // Normalize state for Japanese addresses
        const normalizedState = data._country === 'JP' && data._state ?
            this.normalizeJapanesePrefecture(data._state, data._country) :
            data._state;

        // Store the data for later use
        this.addressData = {
            first_name: data._first_name || '',
            last_name: data._last_name || '',
            address: data._address1 || '',
            apartment: data._address2 || '',
            city: data._city || '',
            state: normalizedState || '',
            zip_code: data._zip_code || '',
            country: data._country || 'US'
        };

        // If shipping data exists, store it too
        if (data._shipping_name && data._shipping_price) {
            this.shippingData = {
                shipping_name: data._shipping_name,
                shipping_price: data._shipping_price,
                shipping_method: data._shipping_name,
                shipping_description: data._shipping_description || ''
            };
        }
    }

    async updateCartWithAddressAndShipping(addressData, shippingData) {
        try {
            const attributes = {
                '_first_name': addressData.first_name,
                '_last_name': addressData.last_name,
                '_address1': addressData.address,
                '_address2': addressData.apartment || '',
                '_city': addressData.city,
                '_state': addressData.state,
                '_zip_code': addressData.zip_code,
                '_country': addressData.country,
                '_country_code': addressData.country,
                '_shipping_name': shippingData.shipping_name,
                '_shipping_price': shippingData.shipping_price,
                '_service_code': shippingData.service_code || '',
                '_shipping_description': shippingData.shipping_description || ''
            };

            // Update cart attributes
            const response = await fetch('/cart/update.js', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({ attributes })
            });

            if (response.ok) {
                // console.log('Cart attributes updated with delivery address and shipping method');

                const data = await response.json();
                window.cartAttributes = data.attributes || {};

                // Also store shipping data in line items for carrier service
                // if (window.shippingPersistenceService) {
                //     try {
                //         const storeResult = await window.shippingPersistenceService.storeShippingData(
                //             shippingData.shipping_name,
                //             shippingData.shipping_price,
                //             shippingData.service_code || '',
                //             shippingData.shipping_description || ''
                //         );

                //         if (!storeResult) {
                //             window.heapGlobal?.trackEvent(
                //                 "store_shipping_data_failed",
                //                 {
                //                     Page: window.getPageForAnalytics(),
                //                     Category: 'Checkout',
                //                     Type: 'Custom event',
                //                     Version: window._lovepop_version,
                //                     Source: 'updateCartWithAddressAndShipping',
                //                     Reason: 'storeShippingData returned false'
                //                 }
                //             );
                //         }
                //     } catch (storeError) {
                //         console.error('Error storing shipping data in line items:', storeError);

                //         window.heapGlobal?.trackEvent(
                //             "store_shipping_data_error",
                //             {
                //                 Page: window.getPageForAnalytics(),
                //                 Category: 'Checkout',
                //                 Type: 'Custom event',
                //                 Version: window._lovepop_version,
                //                 Source: 'updateCartWithAddressAndShipping',
                //                 Error: storeError.message || 'Unknown error',
                //                 "Error Type": storeError.name || 'Error'
                //             }
                //         );
                //     }
                // }

                // Optionally refresh the cart page or update UI
                if (window.location.pathname.includes('/cart')) {
                    // You can trigger a cart update event here
                    document.dispatchEvent(new CustomEvent('cart:updated'));
                }

                if (window._debouncedReloadCart) {
                    window._debouncedReloadCart();
                } else if (window.reloadCart) {
                    window.reloadCart();
                }
            }
        } catch (error) {
            console.error('Error updating cart with address and shipping:', error);
        }
    }
}

customElements.define('delivery-address-form', DeliveryAddressForm);

// Shipzip Service - Centralized Shipzip API handling
class ShipzipService {
    constructor(forSAS = false) {
        this.endpoint = this.getEndpoint();
        this.forSAS = forSAS;
        this.sasData = null;
        if (forSAS) {
            const sasProduct = window.sasFee;
            const variant = sasProduct.variants[0] || {};
            this.additionalItem = {
                key: 'personalize_fee',
                name: sasProduct.title || "",
                sku: variant.sku || "",
                quantity: 1,
                grams: 0,
                tags: sasProduct.product_tags ? sasProduct.product_tags.join(',') : "",
                price: String(variant.price || 0),
                vendor: variant.vendor || "Lovepop",
                requires_shipping: true,
                taxable: true,
                fulfillment_service: "manual",
                properties: {
                    _surprise: true,
                },
                product_id: String(sasProduct.id || ""),
                variant_id: String(variant.id || "")
            }
        }
    }

    getEndpoint() {
        const location = window.location.origin;
        let env = ''

        if (location.includes('test')) {
            env = 'test'
        } else if (location.includes('staging')) {
            env = 'staging'
        } else if (location.includes('prod')) {
            env = 'prod'
        }

        switch (env) {
            case 'test':
                return 'https://gzy6d6r73h.execute-api.us-west-2.amazonaws.com/dev/trungvuong/ShippingOption/shipmentrates';
            case 'staging':
                return 'https://gzy6d6r73h.execute-api.us-west-2.amazonaws.com/dev/trungvuong/ShippingOption/shipmentrates'
            case 'prod':
                return 'https://9ktohjy209.execute-api.us-west-2.amazonaws.com/prod/trungvuong/ShippingOption/shipmentrates'
            default:
                return 'https://9ktohjy209.execute-api.us-west-2.amazonaws.com/prod/trungvuong/ShippingOption/shipmentrates'
        }
    };

    /**
     * Build the origin payload for Shipzip API
     */
    buildOrigin() {
        return {
            country: "US",
            postal_code: "02111",
            province: "MA",
            city: "Boston",
            name: "",
            address1: "68 Harrison Avenue",
            address2: "",
            address3: "",
            phone: "0123456789",
            company_name: "lovepop"
        };
    }

    /**
     * Build the destination payload for Shipzip API from address data
     */
    buildDestination(addressData) {
        if (!addressData || !addressData?.address || !addressData?.country || !addressData?.state || !addressData?.zip_code) {
            if (window.cartAttributes._country && window.cartAttributes._zip_code && window.cartAttributes._state && window.cartAttributes._city && window.cartAttributes._first_name && window.cartAttributes._last_name && window.cartAttributes._address1) {
                return {
                    country: window.cartAttributes._country || "US",
                    postal_code: window.cartAttributes._zip_code || "",
                    province: window.cartAttributes._state || "",
                    city: window.cartAttributes._city || "",
                    name: `${window.cartAttributes._first_name || ""} ${window.cartAttributes._last_name || ""}`.trim(),
                    address1: window.cartAttributes._address1 || "",
                    address2: window.cartAttributes._address2 || "",
                    address3: "",
                    phone: "",
                    email: "",
                    company_name: ""
                };
            }
        }

        return {
            country: addressData.country || "US",
            postal_code: addressData.zip_code || "",
            province: addressData.state || "",
            city: addressData.city || "",
            name: `${addressData.first_name || ""} ${addressData.last_name || ""}`.trim(),
            address1: addressData.address || "",
            address2: addressData.apartment || "",
            address3: "",
            phone: "",
            email: "",
            company_name: ""
        };
    }

    buildAddressFromSASData() {
        return {
            address: this.sasData.addressModel.address,
            apartment: this.sasData.addressModel.apt,
            city: this.sasData.addressModel.city,
            country: this.sasData.addressModel.countryCode,
            first_name: this.sasData.addressModel.firstName,
            last_name: this.sasData.addressModel.lastName,
            state: this.sasData.addressModel.stateProvince,
            zip_code: this.sasData.addressModel.postalCode
        };
    }

    /**
     * Build items payload for Shipzip API from cart items
     */
    async buildItems() {
        if (!window.cartItems || !Array.isArray(window.cartItems)) {
            try {
                const cartResponse = await fetch('/cart.js');
                const cartData = await cartResponse.json();
                window.cartItems = cartData.items || [];
            } catch (error) {
                console.error('Error fetching cart data:', error);
                return [];
            }
        }

        const buildItems = window.cartItems.map(item => {
            const properties = { ...(item.properties || {}) };
            // Shipzip treats any _delivery_date key (even null) as scheduled and drops Expedited
            if (!properties._delivery_date) {
                delete properties._delivery_date;
            }

            return {
                key: item.key || "",
                name: item.product_title || item.title || "",
                sku: item.sku || "",
                quantity: String(item.quantity || 1),
                grams: String(item.grams || 50),
                tags: item.product_tags ? item.product_tags.join(',') : "",
                price: String(item.price || 0),
                vendor: item.vendor || "Lovepop",
                requires_shipping: false,
                taxable: true,
                fulfillment_service: "manual",
                properties,
                product_id: String(item.product_id || ""),
                variant_id: String(item.variant_id || item.id || "")
            };
        });

        if (this.additionalItem && this.forSAS) {
            const currentSAS = buildItems.find(item => {
                return item.key == window.personalization.key;
            });


            if (currentSAS) {
                if (!currentSAS.properties._hasSAS) {
                    const code = randomString(5);
                    const properties = window.buildProperties(this.sasData);

                    properties._code = code;
                    currentSAS.properties = {
                        ...currentSAS.properties,
                        ...properties
                    };

                    this.additionalItem.properties._code = code;
                    buildItems.push(this.additionalItem);
                } else {
                    const addressData = this.buildAddressFromSASData();
                    if (currentSAS.properties._country_code && currentSAS.properties._country_code != addressData.country) {
                        const { _city, _state, _country, _zip_code, _country_code, _first_name, _last_name, _address1, _address2 } = window.buildProperties(this.sasData);
                        currentSAS.properties = {
                            ...currentSAS.properties,
                            _city,
                            _state,
                            _country,
                            _country_code,
                            _first_name,
                            _last_name,
                            _address1,
                            _address2,
                            _zip_code
                        };
                    }
                }
            } else {
                let querySimulate = await fetch(window.personalization.product_handle + '?view=simulate-sas-item');
                querySimulate = await querySimulate.text();
                const sasLineSimulate = JSON.parse(querySimulate || '{}');

                if (sasLineSimulate) {
                    const code = randomString(5);
                    const properties = window.buildProperties(this.sasData);

                    properties._code = code;
                    sasLineSimulate.properties = {
                        ...sasLineSimulate.properties,
                        ...properties
                    };

                    buildItems.push(sasLineSimulate);

                    this.additionalItem.properties._code = code;
                    buildItems.push(this.additionalItem);
                }
            }
        }

        return buildItems;
    }

    /**
     * Get the shop domain for Shipzip API
     */
    getShopDomain() {
        if (window.Shopify && window.Shopify.shop) {
            return window.Shopify.shop;
        }

        const hostname = window.location.hostname;
        if (hostname.includes('lovepop-staging')) {
            return 'lovepop-staging.myshopify.com';
        } else if (hostname.includes('lovepop')) {
            return 'lovepop.myshopify.com';
        }

        return 'lovepoptest.myshopify.com';
    }

    /**
     * Process Shipzip rates - remove duplicates and format for our system
     */
    processRates(rates) {
        const seenMergeTags = new Set();
        let processedRates = [];

        for (const rate of rates) {
            const rateItems = rate.rate_items || [];

            const isContainSASFee = rateItems.some(item => {
                return item.properties && (item.properties._surprise == true || item.properties._surprise == "true");
            });

            const isSASRate = rateItems.some(item => {
                return item.properties && item.properties._hasSAS && item.properties._address1;
            });

            if (!this.forSAS && !(isContainSASFee && isSASRate)) {
                for (const shippingRate of rate.shipment_rate_responses.rates) {
                    if (!shippingRate.service_name.includes('Invalid Zip Code')) {
                        processedRates.push({
                            name: shippingRate.service_name?.trim() || 'Unknown Service',
                            description: shippingRate.description?.trim() || '',
                            price: ((shippingRate.total_price || 0) / 100).toFixed(2),
                            handle: shippingRate.service_code || '',
                            service_code: shippingRate.service_code || '',
                            currency: shippingRate.currency || 'USD',
                            shipping_option_type_id: shippingRate.shipping_option_type_id || '',
                            max_delivery_date: shippingRate.max_delivery_date || '',
                            min_delivery_date: shippingRate.min_delivery_date || '',
                            planned_ship_date: shippingRate.planned_ship_date || ''
                        });
                    }
                }
                break;
            } else if (this.forSAS && isContainSASFee && isSASRate) {
                const sasRate = rateItems.find(item => item.key === window.personalization.key);

                if (sasRate) {
                    for (const shippingRate of rate.shipment_rate_responses.rates) {
                        if (!shippingRate.service_name.includes('Invalid Zip Code')) {
                            processedRates.push({
                                name: shippingRate.service_name?.trim() || 'Unknown Service',
                                description: shippingRate.description?.trim() || '',
                                price: ((shippingRate.total_price || 0) / 100).toFixed(2),
                                handle: shippingRate.service_code || '',
                                service_code: shippingRate.service_code || '',
                                currency: shippingRate.currency || 'USD',
                                shipping_option_type_id: shippingRate.shipping_option_type_id || '',
                                max_delivery_date: shippingRate.max_delivery_date || '',
                                min_delivery_date: shippingRate.min_delivery_date || '',
                                planned_ship_date: shippingRate.planned_ship_date || ''
                            });
                        }
                    }

                    break;
                }
            }
        }

        return processedRates;
    }

    extractWarningMessage(rates) {
        if (!rates?.length) return null;
        const msg = rates[0].warning_message;
        return typeof msg === "string" && msg.trim() ? msg : null;
    }

    sortShippingRates(rates) {
        if (!rates || !Array.isArray(rates)) {
            return [];
        }

        return rates.sort((a, b) => {
            const aName = (a.name || '').toLowerCase();
            const bName = (b.name || '').toLowerCase();

            const aIsTracked = aName.includes('tracked') && aName.includes('standard');
            const bIsTracked = bName.includes('tracked') && bName.includes('standard');

            // If both are tracked or both are not tracked, maintain original order
            if (aIsTracked === bIsTracked) {
                return 0;
            }

            // If a is tracked and b is not, a comes first (return -1)
            if (aIsTracked && !bIsTracked) {
                return -1;
            }

            // If b is tracked and a is not, b comes first (return 1)
            return 1;
        });
    }
    /**
     * Fetch shipping rates from Shipzip API
     */
    async fetchRates(addressData, sasData = null) {
        try {
            if (this.forSAS) {
                this.sasData = sasData;
            }

            const items = await this.buildItems();
            if (!items || items.length === 0) {
                return { rates: [], warning_message: null };
            }
            const payload = {
                shop: this.getShopDomain(),
                rate: {
                    origin: this.buildOrigin(),
                    destination: this.buildDestination(addressData),
                    items: items
                }
            };

            if (window.customer && window.customer.id) {
                payload.rate.customer = {
                    id: window.customer.id,
                    tags: window.customer.tags || []
                };
            }

            // console.log('Shipzip API payload:', payload);

            const response = await fetch(this.endpoint, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Accept': 'application/json'
                },
                body: JSON.stringify(payload)
            });

            if (!response.ok) {
                console.error('Shipzip API error:', response.status, response.statusText);
                return { rates: [], warning_message: null };
            }

            const data = await response.json();
            // console.log('Shipzip API response:', data);

            if (!data.shipment_rates || !Array.isArray(data.shipment_rates)) {
                console.warn('No rates returned from Shipzip API');
                return { rates: [], warning_message: null };
            }

            const processedRates = this.processRates(data.shipment_rates);
            const warning_message = this.extractWarningMessage(data.shipment_rates);
            // ABCD test: promote this visitor's assigned option to the top. Applied here,
            // at the single point every rate list comes from, rather than at one consumer —
            // the personalization flow calls fetchRates() directly and would otherwise be
            // left on the raw API order. Returns the list untouched when the test is off.
            const rates = window.applyShippingOrderTestSort?.(processedRates) ?? processedRates;
            return { rates, warning_message };
        } catch (error) {
            console.error('Error fetching Shipzip rates:', error);
            return { rates: [], warning_message: null };
        }
    }
}

// Global Shipzip service instance
window.shipzipService = new ShipzipService();
window.shipzipServiceSAS = new ShipzipService(true, false); // For SAS products

// Shipping Data Persistence Service - Store shipping data in line items for carrier service
class ShippingPersistenceService {
    /**
     * Find line item that stores shipping data
     */
    async findShippingLineItem() {
        if (!window.cartItems || !Array.isArray(window.cartItems)) {
            try {
                const cartResponse = await fetch('/cart.js');
                const cartData = await cartResponse.json();
                window.cartItems = cartData.items || [];
            } catch (error) {
                console.error('Error fetching cart data:', error);
                return null;
            }
        }

        return window.cartItems.find(item => {
            return item.properties && item.properties._store_shipping === 'true';
        });
    }

    /**
     * Find a suitable line item to store shipping data
     */
    async findSuitableLineItem() {
        if (!window.cartItems || !Array.isArray(window.cartItems)) {
            try {
                const cartResponse = await fetch('/cart.js');
                const cartData = await cartResponse.json();
                window.cartItems = cartData.items || [];
            } catch (error) {
                console.error('Error fetching cart data:', error);
                return null;
            }
        }

        return window.cartItems.find(item => {
            if (!item.requires_shipping) {
                return false;
            }

            // Skip items with _surprise property
            if (item.properties && item.properties._surprise) {
                return false;
            }

            if (item.properties && item.properties._address1) {
                return false;
            }
            // Skip "Personalized Note" items
            if (item.title === "Personalized Note" || item.product_title === "Personalized Note") {
                return false;
            }
            return true;
        });
    }

    /**
     * Store shipping data in a line item
     */
    async storeShippingData(shippingName, shippingPrice, serviceCode = '', shippingDescription) {
        try {
            let targetItem = await this.findShippingLineItem();

            if (!targetItem) {
                // Find a suitable item to store shipping data
                targetItem = await this.findSuitableLineItem();
                if (!targetItem) {
                    console.warn('No suitable line item found to store shipping data');
                    return false;
                }
            }

            const updates = {
                id: targetItem.key,
                quantity: targetItem.quantity,
                properties: {
                    ...targetItem.properties,
                    '_store_shipping': 'true',
                    '_shipping_name': shippingName,
                    '_shipping_price': shippingPrice,
                    '_service_code': serviceCode,
                    '_shipping_description': shippingDescription || ''
                }
            };

            const response = await fetch('/cart/change.js', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(updates)
            });

            if (window._debouncedReloadCart) {
                window._debouncedReloadCart();
            } else if (window.reloadCart) {
                window.reloadCart();
            }

            if (response.ok) {
                // console.log('Shipping data stored in line item:', targetItem.key);
                return true;
            } else {
                console.error('Failed to store shipping data in line item');

                window.heapGlobal?.trackEvent(
                    "store_shipping_http_error"
                );

                return false;
            }
        } catch (error) {
            console.error('Error storing shipping data:', error);

            window.heapGlobal?.trackEvent(
                "store_shipping_exception"
            );

            return false;
        }
    }

    /**
     * Remove shipping data from line item
     */
    async removeShippingData(itemKey) {
        try {
            const item = window.cartItems.find(item => item.key === itemKey);
            if (!item) return false;

            const updatedProperties = { ...item.properties };
            delete updatedProperties._store_shipping;
            delete updatedProperties._shipping_name;
            delete updatedProperties._shipping_price;
            delete updatedProperties._service_code;
            delete updatedProperties._shipping_description;

            const updates = {
                id: itemKey,
                properties: updatedProperties
            };

            const response = await fetch('/cart/change.js', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(updates)
            });

            if (window._debouncedReloadCart) {
                window._debouncedReloadCart();
            } else if (window.reloadCart) {
                window.reloadCart();
            }

            if (response.ok) {
                // console.log('Shipping data removed from line item:', itemKey);
                return true;
            } else {
                console.error('Failed to remove shipping data from line item');
                return false;
            }
        } catch (error) {
            console.error('Error removing shipping data:', error);
            return false;
        }
    }

    /**
     * Ensure shipping data persists by transferring to another item if needed
     */
    async ensureShippingPersistence() {
        try {
            const currentShippingItem = await this.findShippingLineItem();

            if (!currentShippingItem) {
                // No shipping data in line items, check if we need to transfer from cart attributes
                if (window.cartAttributes && window.cartAttributes._shipping_name) {
                    const newTargetItem = await this.findSuitableLineItem();
                    if (newTargetItem) {
                        // console.log('Moving shipping data from cart attributes to line item');
                        return await this.storeShippingData(
                            window.cartAttributes._shipping_name,
                            window.cartAttributes._shipping_price,
                            window.cartAttributes._service_code || '',
                            window.cartAttributes._shipping_description || ''
                        );
                    }
                }
                return true; // No shipping data to persist
            }

            // Check if the current shipping item still exists
            const stillExists = window.cartItems.some(item => item.key === currentShippingItem.key);

            if (stillExists) {
                // Item exists, but verify shipping data matches cart attributes
                if (window.cartAttributes && window.cartAttributes._shipping_name) {
                    const lineItemShippingName = currentShippingItem.properties._shipping_name;
                    const lineItemShippingPrice = currentShippingItem.properties._shipping_price;
                    const cartShippingName = window.cartAttributes._shipping_name;
                    const cartShippingPrice = window.cartAttributes._shipping_price;

                    // Check if shipping data matches
                    if (lineItemShippingName !== cartShippingName || lineItemShippingPrice !== cartShippingPrice) {
                        // console.log('Shipping data mismatch between line item and cart attributes, updating line item');
                        return await this.storeShippingData(
                            cartShippingName,
                            cartShippingPrice,
                            window.cartAttributes._service_code || '',
                            window.cartAttributes._shipping_description || ''
                        );
                    }
                }

                // console.log('Shipping data item still exists and matches cart attributes, no transfer needed');
                return true;
            }

            // Current shipping item was removed, find new suitable item
            const newTargetItem = await this.findSuitableLineItem();

            if (!newTargetItem) {
                console.warn('No suitable item found for shipping data transfer');
                return false;
            }

            // Transfer shipping data from cart attributes
            if (window.cartAttributes && window.cartAttributes._shipping_name) {
                // console.log('Shipping item was removed, recreating from cart attributes');
                return await this.storeShippingData(
                    window.cartAttributes._shipping_name,
                    window.cartAttributes._shipping_price,
                    window.cartAttributes._service_code || '',
                    window.cartAttributes._shipping_description || ''
                );
            }

            return false;
        } catch (error) {
            console.error('Error ensuring shipping persistence:', error);
            return false;
        }
    }

    /**
     * Get shipping data from line item or cart attributes
     */
    async getShippingData() {
        // First try to get from line item
        const shippingItem = await this.findShippingLineItem();
        if (shippingItem && shippingItem.properties) {
            return {
                shipping_name: shippingItem.properties._shipping_name,
                shipping_price: shippingItem.properties._shipping_price,
                service_code: shippingItem.properties._service_code,
                shipping_description: shippingItem.properties._shipping_description,
                source: 'line_item'
            };
        }

        // Fallback to cart attributes
        if (window.cartAttributes && window.cartAttributes._shipping_name) {
            return {
                shipping_name: window.cartAttributes._shipping_name,
                shipping_price: window.cartAttributes._shipping_price,
                service_code: window.cartAttributes._service_code,
                shipping_description: window.cartAttributes._shipping_description || '',
                source: 'cart_attributes'
            };
        }

        return null;
    }
}

// Global shipping persistence service instance
window.shippingPersistenceService = new ShippingPersistenceService();

// Global cart shipping rate checker
class CartShippingRateManager {
    constructor() {
        this.checkTimeout = null;
        this.isUpdatingShipping = false; // Flag to prevent infinite loops
        this.shipzipService = window.shipzipService; // Use shared Shipzip service
        this.setupCartUpdateListeners();
    }

    // Debounced version to prevent too frequent calls
    debouncedCheckShippingRates() {
        // Skip if we're currently updating shipping to prevent loops
        if (this.isUpdatingShipping) {
            // console.log('Skipping shipping check - currently updating shipping rates');
            return;
        }

        clearTimeout(this.checkTimeout);
        this.checkTimeout = setTimeout(() => {
            this.checkAndUpdateShippingRates();
        }, 1000); // Wait 1 second after last call
    }

    setupCartUpdateListeners() {
        // Listen for cart update events
        document.addEventListener('cart:updated', () => {
            // console.log('Cart updated, checking shipping rates...');
            this.debouncedCheckShippingRates();
        });

        // Listen for manual cart updates
        document.addEventListener('cart:quantity:changed', () => {
            // console.log('Cart quantity changed, checking shipping rates...');
            this.debouncedCheckShippingRates();
        });

        // Intercept fetch requests to cart endpoints
        this.interceptCartRequests();
    }

    interceptCartRequests() {
        if (window.cartShippingInterceptorActive) return; // Prevent multiple interceptors
        window.cartShippingInterceptorActive = true;

        const originalFetch = window.fetch;
        const self = this;

        window.fetch = async function (...args) {
            const response = await originalFetch.apply(this, args);

            // Check if this was a cart update request
            const url = args[0];
            if (typeof url === 'string' &&
                (url.includes('/cart/update') ||
                    url.includes('/cart/change') ||
                    url.includes('/cart/add') ||
                    url.includes('/cart/clear'))) {

                // Skip if we're the ones making the update to prevent infinite loops
                if (self.isUpdatingShipping) {
                    // console.log('Skipping shipping check - update made by CartShippingRateManager');
                    return response;
                }

                // Wait for cart to be updated, then check shipping rates
                setTimeout(() => {
                    // console.log('Cart API call detected, checking shipping rates...');
                    self.debouncedCheckShippingRates();
                }, 2000);
            }

            return response;
        };
    }

    // Clears any leftover shared-address cart attributes and line-item shipping
    // properties from a cart that used to contain a recipient (SAS) item but no
    // longer does (e.g. the recipient card was removed, leaving send-to-me items
    // only). Send-to-me-only carts must not carry this data — checkout handles
    // address/shipping for them instead.
    async clearStaleAddressData() {
        if (this.isUpdatingShipping) {
            return;
        }

        const addressAttributeKeys = [
            '_address1', '_address2', '_city', '_state', '_country', '_country_code',
            '_zip_code', '_first_name', '_last_name', '_shipping_name', '_shipping_price',
            '_service_code', '_shipping_description'
        ];

        const attributes = window.cartAttributes || {};
        const hasStaleAttributes = addressAttributeKeys.some((key) => attributes[key]);

        const staleShippingItem = (window.cartItems || []).find(
            (item) => item.properties && item.properties._store_shipping === 'true'
        );

        if (!hasStaleAttributes && !staleShippingItem) {
            return;
        }

        this.isUpdatingShipping = true;

        try {
            if (hasStaleAttributes) {
                const clearedAttributes = {};
                addressAttributeKeys.forEach((key) => { clearedAttributes[key] = ''; });

                await fetch('/cart/update.js', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ attributes: clearedAttributes })
                });

                if (window.cartAttributes) {
                    addressAttributeKeys.forEach((key) => { window.cartAttributes[key] = ''; });
                }
            }

            if (staleShippingItem && window.shippingPersistenceService) {
                await window.shippingPersistenceService.removeShippingData(staleShippingItem.key);
            }

            if (window._debouncedReloadCart) {
                window._debouncedReloadCart();
            } else if (window.reloadCart) {
                window.reloadCart();
            }
        } catch (error) {
            console.error('Error clearing stale send-to-me address/shipping data:', error);
        } finally {
            this.isUpdatingShipping = false;
        }
    }

    async checkAndUpdateShippingRates() {
        try {
            // Prevent infinite loops - don't check while we're updating
            if (this.isUpdatingShipping) {
                // console.log('Already updating shipping rates, skipping check');
                return;
            }

            if (!window.cartItems || window.cartItems.length <= 0) {
                return;
            }

            // Send-to-me carts never collect an in-cart address/shipping method —
            // nothing to validate or sync until the customer reaches checkout. Any
            // address already entered is left alone here; it's only cleared at the
            // actual checkout click (see cart.js), not on every background cart update.
            if (!window.getCartRequiresAddressFlow?.()) {
                return;
            }

            let hasShippingAddress = false;
            let addressData = {};
            let onlyCheckSASItems = false;

            // Extract address data from cart attributes
            if (window.cartAttributes) {
                hasShippingAddress = window.cartAttributes._address1 &&
                    window.cartAttributes._city &&
                    window.cartAttributes._state &&
                    window.cartAttributes._zip_code &&
                    window.cartAttributes._country;

                if (hasShippingAddress) {
                    addressData = {
                        address: window.cartAttributes._address1,
                        apartment: window.cartAttributes._address2 || '',
                        city: window.cartAttributes._city,
                        state: window.cartAttributes._state,
                        zip_code: window.cartAttributes._zip_code,
                        country: window.cartAttributes._country
                    };
                }
            }

            if (!hasShippingAddress) {
                // Check if there are any SAS items in cart that need shipping rate validation
                const hasSASItems = window.cartItems && window.cartItems.some(item =>
                    item.properties && item.properties._hasSAS && item.properties._address1
                );

                if (!hasSASItems) {
                    // console.log('No shipping address found and no SAS items in cart, skipping shipping rate check');
                    return;
                }

                // Use a fake US address for SAS items validation
                // console.log('No cart shipping address, using fake address for SAS items validation only');
                addressData = {
                    address: '1600 Pennsylvania Avenue NW',
                    apartment: '',
                    city: 'Washington',
                    state: 'DC',
                    zip_code: '20500',
                    country: 'United States'
                };
                onlyCheckSASItems = true;
            }

            // Get current shipping rates with new response format
            const shippingResponse = await this.fetchShippingRates(addressData);
            const { shipment_rates, raw_response } = shippingResponse;

            if (!shipment_rates || shipment_rates.length === 0) {
                // console.log('No shipping rates available from Shipzip');
                return;
            }

            let needsUpdate = false;
            let updatedAttributes = {};
            let itemsToUpdate = [];

            // Process each shipment rate group
            for (const shipmentRate of shipment_rates) {
                const rateItems = shipmentRate.rate_items || [];
                const availableRates = shipmentRate.shipment_rate_responses?.rates || [];

                if (availableRates.length === 0) {
                    // console.log('No rates available for this shipment group');
                    continue;
                }

                // Check if this shipment contains SAS items (items with _hasSAS property)
                const isSASShipment = rateItems.some(item =>
                    item.properties && item.properties._hasSAS && item.properties._address1
                );

                // Check if this shipment contains surprise fee items
                const containsSurpriseFee = rateItems.some(item =>
                    item.properties && (item.properties._surprise === true || item.properties._surprise == "true")
                );

                // Check if this is regular cart shipment (no SAS, no surprise fee)
                const isRegularCartShipment = !(isSASShipment && containsSurpriseFee);

                // Handle regular cart shipping (stored in cart attributes)
                // Skip regular cart updates if we're only checking SAS items (no real shipping address)
                if (isRegularCartShipment && !onlyCheckSASItems && window.cartAttributes) {
                    if (window.cartAttributes._shipping_name) {
                        const cartShippingName = window.cartAttributes._shipping_name;
                        const cartShippingPrice = parseFloat(window.cartAttributes._shipping_price) || 0;

                        // Find matching shipping option in available rates
                        const matchingRate = availableRates.find(rate =>
                            rate.service_name?.trim() === cartShippingName
                        );

                        if (matchingRate) {
                            const currentPrice = (matchingRate.total_price || 0) / 100;
                            if (Math.abs(currentPrice - cartShippingPrice) > 0.01) {
                                // console.log(`Shipping price mismatch for ${cartShippingName}: cart has ${cartShippingPrice}, should be ${currentPrice.toFixed(2)}`);
                                updatedAttributes._shipping_price = currentPrice.toFixed(2);
                                updatedAttributes._shipping_description = matchingRate.description?.trim() || '';
                                needsUpdate = true;
                            }
                        } else if (availableRates.length > 0) {
                            // Shipping option no longer available, reset to first available option
                            // console.log(`Shipping option ${cartShippingName} no longer available, updating to first available option`);
                            const firstRate = availableRates[0];
                            updatedAttributes._shipping_name = firstRate.service_name?.trim() || 'Unknown Service';
                            updatedAttributes._shipping_price = ((firstRate.total_price || 0) / 100).toFixed(2);
                            updatedAttributes._service_code = firstRate.service_code || '';
                            updatedAttributes._shipping_description = firstRate.description?.trim() || '';
                            needsUpdate = true;
                        }
                    } else if (availableRates.length > 0) {
                        // No shipping method selected yet for regular cart, initialize with first available option
                        // console.log('No shipping method selected for regular cart, initializing with first available option');
                        const firstRate = availableRates[0];
                        updatedAttributes._shipping_name = firstRate.service_name?.trim() || 'Unknown Service';
                        updatedAttributes._shipping_price = ((firstRate.total_price || 0) / 100).toFixed(2);
                        updatedAttributes._service_code = firstRate.service_code || '';
                        updatedAttributes._shipping_description = firstRate.description?.trim() || '';
                        needsUpdate = true;
                    }
                }

                // Handle SAS items shipping (stored in line item properties)
                if (isSASShipment && containsSurpriseFee && window.cartItems && Array.isArray(window.cartItems)) {
                    // Find the SAS item in rate_items that has _hasSAS property
                    const sasRateItem = rateItems.find(item =>
                        item.properties && item.properties._hasSAS && item.properties._address1
                    );

                    if (sasRateItem) {
                        const cartItem = window.cartItems.find(item => {
                            if (sasRateItem.key && item.key === sasRateItem.key) {
                                return true;
                            }

                            if (sasRateItem.properties && sasRateItem.properties._code &&
                                item.properties && item.properties._code === sasRateItem.properties._code &&
                                item.product_id === sasRateItem.product_id) {
                                return true;
                            }
                            return false;
                        });

                        if (cartItem && cartItem.properties) {
                            // Check if item has shipping data
                            if (cartItem.properties._shipping_name) {
                                const itemShippingName = cartItem.properties._shipping_name;
                                const itemShippingPrice = parseFloat(cartItem.properties._shipping_price) || 0;

                                // Find matching shipping option in available rates
                                const matchingRate = availableRates.find(rate =>
                                    rate.service_name?.trim().toLowerCase().replaceAll(' ', '') ===
                                    itemShippingName.toLowerCase().replaceAll(' ', '')
                                );

                                if (matchingRate) {
                                    const currentPrice = (matchingRate.total_price || 0) / 100;
                                    if (Math.abs(currentPrice - itemShippingPrice) > 0.01) {
                                        // console.log(`SAS item shipping price mismatch for ${itemShippingName}: item has ${itemShippingPrice}, should be ${currentPrice.toFixed(2)}`);
                                        itemsToUpdate.push({
                                            item: cartItem,
                                            shippingName: itemShippingName,
                                            shippingPrice: currentPrice.toFixed(2),
                                            shippingDescription: matchingRate.description?.trim() || '',
                                            serviceCode: matchingRate.service_code || '',
                                            shippingSetAt: new Date().toISOString()
                                        });
                                    }
                                } else if (availableRates.length > 0 && !itemShippingName.toLowerCase().includes('standard')) {
                                    // Shipping option no longer available for this item
                                    // console.log(`SAS item shipping option ${itemShippingName} no longer available, updating to first available option`);
                                    const firstRate = availableRates[0];
                                    itemsToUpdate.push({
                                        item: cartItem,
                                        shippingName: firstRate.service_name?.trim() || 'Unknown Service',
                                        shippingPrice: ((firstRate.total_price || 0) / 100).toFixed(2),
                                        shippingDescription: firstRate.description?.trim() || '',
                                        serviceCode: firstRate.service_code || '',
                                        shippingSetAt: new Date().toISOString()
                                    });
                                }
                            } else {
                                // Item doesn't have shipping data yet, add it
                                // console.log(`SAS item ${cartItem.key} missing shipping data, adding first available option`);
                                const firstRate = availableRates[0];
                                itemsToUpdate.push({
                                    item: cartItem,
                                    shippingName: firstRate.service_name?.trim() || 'Unknown Service',
                                    shippingPrice: ((firstRate.total_price || 0) / 100).toFixed(2),
                                    shippingDescription: firstRate.description?.trim() || '',
                                    serviceCode: firstRate.service_code || '',
                                    shippingSetAt: new Date().toISOString()
                                });
                            }
                        } else {
                            console.warn('Could not find cart item matching SAS rate item:', sasRateItem);
                        }
                    }
                }
            }

            // Update cart attributes if needed
            if (needsUpdate && Object.keys(updatedAttributes).length > 0) {
                // console.log('Updating cart attributes with new shipping prices:', updatedAttributes);

                // Set flag to prevent infinite loop
                this.isUpdatingShipping = true;

                try {
                    const response = await fetch('/cart/update.js', {
                        method: 'POST',
                        headers: {
                            'Content-Type': 'application/json'
                        },
                        body: JSON.stringify({ attributes: updatedAttributes })
                    });

                    if (response.ok) {
                        // console.log('Cart attributes updated successfully');
                        if (window.cartAttributes) {
                            Object.assign(window.cartAttributes, updatedAttributes);
                        }

                        if (window._debouncedReloadCart) {
                            window._debouncedReloadCart();
                        } else if (window.reloadCart) {
                            window.reloadCart();
                        }
                    } else {
                        console.error('Failed to update cart attributes');
                    }
                } finally {
                    setTimeout(() => {
                        this.isUpdatingShipping = false;
                    }, 1000);
                }
            }

            // Update SAS items shipping if needed
            if (itemsToUpdate.length > 0) {
                // Set flag to prevent infinite loop
                this.isUpdatingShipping = true;

                try {
                    for (const updateData of itemsToUpdate) {
                        await this.updateCartItemShipping(
                            updateData.item,
                            updateData.shippingDescription,
                            updateData.serviceCode,
                            updateData.shippingName,
                            updateData.shippingPrice
                        );
                    }

                    if (window._debouncedReloadCart) {
                        window._debouncedReloadCart();
                    } else if (window.reloadCart) {
                        window.reloadCart();
                    }
                } finally {
                    setTimeout(() => {
                        this.isUpdatingShipping = false;
                    }, 1000);
                }
            }

        } catch (error) {
            console.error('Error checking and updating shipping rates:', error);
            // Reset flag in case of error
            this.isUpdatingShipping = false;
        }
    }

    async prepareShippingRates(addressData) {
        // No longer needed with Shipzip API - rates are fetched directly
        // Keeping method for backward compatibility
        return true;
    }

    async fetchShippingRates(addressData) {
        try {


            // Fetch raw shipment rates from Shipzip API
            const items = await this.shipzipService.buildItems();
            if (!items || items.length === 0) {
                return { shipment_rates: [], raw_response: null };
            }
            const payload = {
                shop: this.shipzipService.getShopDomain(),
                rate: {
                    origin: this.shipzipService.buildOrigin(),
                    destination: this.shipzipService.buildDestination(addressData),
                    items: items
                }
            };

            if (window.customer && window.customer.id) {
                payload.rate.customer = {
                    id: window.customer.id,
                    tags: window.customer.tags || []
                };
            }

            // console.log('Cart Shipzip API payload:', payload);

            const response = await fetch(this.shipzipService.endpoint, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Accept': 'application/json'
                },
                body: JSON.stringify(payload)
            });

            if (!response.ok) {
                console.error('Shipzip API error:', response.status, response.statusText);
                return { shipment_rates: [], raw_response: null };
            }

            const data = await response.json();
            // console.log('Cart Shipzip API response:', data);

            if (!data.shipment_rates || !Array.isArray(data.shipment_rates)) {
                console.warn('No rates returned from Shipzip API');
                return { shipment_rates: [], raw_response: data };
            }

            // Return both raw shipment rates and the full response for processing
            return { shipment_rates: data.shipment_rates, raw_response: data };

        } catch (error) {
            console.error('Error fetching shipping rates:', error);
            return { shipment_rates: [], raw_response: null };
        }
    }

    async updateCartItemShipping(item, shippingDescription = '', serviceCode = '', shippingName = '', shippingPrice = null) {
        // console.log('Updating cart item shipping:', item, shippingDescription, serviceCode);
        try {
            const updatedProperties = {
                ...item.properties,
                '_shipping_description': shippingDescription || ''
            };

            // Add service code if provided
            if (serviceCode) {
                updatedProperties._service_code = serviceCode;
            }

            // Update shipping name and price if provided
            if (shippingName) {
                updatedProperties._shipping_name = shippingName;
            }
            if (shippingPrice !== null) {
                updatedProperties._shipping_price = shippingPrice;
            }

            // Shipzip treats any _delivery_date key (even null) as scheduled and drops Expedited
            if (!updatedProperties._delivery_date) {
                delete updatedProperties._delivery_date;
            }

            const response = await fetch('/cart/change.js', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    id: item.key,
                    properties: updatedProperties
                })
            });

            if (response.ok) {
                // console.log(`Updated shipping for cart item ${item.key}: ${shippingName} - $${shippingPrice}`);
                return true;
            } else {
                console.error(`Failed to update shipping for cart item ${item.key}`);
                return false;
            }
        } catch (error) {
            console.error('Error updating cart item shipping:', error);
            return false;
        }
    }
}

// Initialize the global cart shipping rate manager
window.cartShippingRateManager = new CartShippingRateManager();

// Expose a global function for manual triggering
window.checkCartShippingRates = () => {
    if (window.cartShippingRateManager) {
        window.cartShippingRateManager.checkAndUpdateShippingRates();
    }
};